import {
  CloseOutlined,
  DeleteOutlined,
  SendOutlined,
} from "@ant-design/icons";
import aiAgentIcon from "/Logo/ai_agent.png";
import {
  Button,
  DatePicker,
  Drawer,
  FloatButton,
  Input,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import dayjs from "dayjs";
import { apiUserGetCompany, apiUserPostDepartment } from "@/api";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { usePdfPreview } from "@/components/PdfPreviewModal";
import {
  buildDrawingPdfIndex,
  lookupDrawingPdf,
  type DrawingPdf,
} from "@/utility/drawingPdf";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "@/i18n";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";

type UIPayload =
  | { kind: "navigate"; label: string; path: string; reason?: string }
  | {
      kind: "entityList";
      entityType: EntityType;
      rows: Record<string, any>[];
      columns: ColumnDef[];
      itemPath?: (id: string) => string;
    }
  | {
      kind: "entityDetail";
      entityType: EntityType;
      record: Record<string, any>;
      itemPath?: string;
    }
  | {
      kind: "createForm";
      formType: FormType;
    }
  | {
      kind: "employeeEdit";
      record: Record<string, any>;
    }
  | {
      kind: "employeeStatus";
      record: Record<string, any>;
      action: "resign" | "unresign";
    }
  | {
      kind: "bankBalance";
      banks: { id: string; name: string; balance: number; remarks?: string }[];
    };

type ColumnDef = {
  title: string;
  key: string;
  format?: "date" | "datetime" | "tag" | "money" | "address" | "contacts";
};

type EntityType =
  | "company"
  | "project"
  | "projectNode"
  | "employee"
  | "department"
  | "partner"
  | "product"
  | "purchaseOrder"
  | "accountPayable"
  | "accountReceivable"
  | "stockRequest"
  | "stockReceipt"
  | "warehouse"
  | "bank"
  | "materialBom"
  | "steelBoltBom"
  | "steelAssembly"
  | "designBom"
  | "workItem"
  | "scanHistory";

type ChatMessage = {
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  tool_call_id?: string;
  tool_calls?: any[];
  name?: string;
  ui?: UIPayload;
};

const OPENROUTER_URL = "https://openrouter.ai/api/v1/chat/completions";
const STORAGE_KEY = "sfc_chatbot_messages";
const CHAT_WIDTH = 440;
const MIN_WIDTH = 320;
const MAX_WIDTH = 1400;
const MIN_PAGE_WIDTH = 320;

function clampChatWidth(width: number) {
  const max = Math.max(
    MIN_WIDTH,
    Math.min(
      MAX_WIDTH,
      (typeof window === "undefined" ? MAX_WIDTH : window.innerWidth) -
        MIN_PAGE_WIDTH,
    ),
  );
  return Math.min(max, Math.max(MIN_WIDTH, width));
}
const WIDTH_KEY = "sfc_chatbot_width";

// 改由後端 /ai 端點（useChat）處理的頁面；工具由 server 提供。
// 其餘頁面維持原本前端直連 OpenRouter 的路徑。
// scenario 需與 server ai/index.ts 的 scenarios 一致，server 依情境決定可用工具。
export type BackendScenario =
  | "demo"
  | "finance"
  | "project"
  | "purchase"
  | "inventory";

const BACKEND_SCENARIO_BY_PAGE: Record<string, BackendScenario> = {
  finance: "finance",
  productionPlan: "project",
  project: "project",
  bom: "project",
  purchaseOrder: "purchase",
  warehouse: "inventory",
  stockReceipt: "inventory",
  stockRequest: "inventory",
  stockHistory: "inventory",
};

const BACKEND_PAGE_KEYS = new Set<string>(
  Object.keys(BACKEND_SCENARIO_BY_PAGE),
);

const SEPARATE_CHAT_PAGE_KEYS = new Set<string>(["productionPlan"]);

export function getBackendChatKey(pageKey: string): string {
  if (SEPARATE_CHAT_PAGE_KEYS.has(pageKey)) return pageKey;
  return BACKEND_SCENARIO_BY_PAGE[pageKey] ?? pageKey;
}

// 由頁面（例如生產計畫）帶入的即時情境，會夾在每則使用者訊息前送給模型。
export type ChatContextInfo = {
  projectId?: string;
  projectName?: string;
  node?: {
    id: string;
    name: string;
    type?: string;
  } | null;
};

const CONTEXT_OPEN = "<<page-context>>";
const CONTEXT_CLOSE = "<</page-context>>";
const CONTEXT_STRIP_RE = /^<<page-context>>[\s\S]*?<<\/page-context>>\s*/;

const TREE_OPEN = "<<node-tree>>";
const TREE_CLOSE = "<</node-tree>>";
const TREE_STRIP_RE = /<<node-tree>>[\s\S]*?<<\/node-tree>>\s*/g;

const MODE_OPEN = "<<mode>>";
const MODE_CLOSE = "<</mode>>";
const MODE_STRIP_RE = /^<<mode>>[\s\S]*?<<\/mode>>\s*/;

const WRITE_ACTIONS = new Set([
  "create",
  "update",
  "delete",
  "addSteelAssembly",
  "removeSteelAssembly",
]);

const TOOL_ENTITY_LABEL: Record<string, string> = {
  projectNodeCrudTool: "案件節點",
  steelAssemblyCrudTool: "構件",
};

const TOOL_ACTION_LABEL: Record<string, string> = {
  list: "查詢",
  read: "讀取",
  search: "搜尋",
  create: "新增",
  update: "修改",
  delete: "刪除",
  addSteelAssembly: "加入構件",
  removeSteelAssembly: "移出構件",
};

const WRITE_DIRECTIVE = `${MODE_OPEN}
允許執行寫入型工具，但每次寫入前必須先簡述將變更哪些資料、共幾筆，執行後回報實際結果。
刪除屬於不可復原的操作，執行前一律先向使用者確認。
需要使用者確認時，請在訊息最後獨立一行輸出標記 [[confirm:要執行的動作摘要]]，
不要請使用者自行輸入「確認」等文字，介面會依此標記顯示確認按鈕。
${MODE_CLOSE}
`;

const CONFIRM_MARKER_RE = /\[\[confirm:([\s\S]*?)\]\]/;
const CONFIRM_MARKER_STRIP_RE = /\[\[confirm:[\s\S]*?\]\]/g;
const CONFIRM_FALLBACK_RE = /請[^\n]{0,10}回覆[^\n]{0,20}(確認|開始刪除|執行)/;

function stripConfirmMarker(text: string): string {
  if (typeof text !== "string") return text;
  return text
    .replace(CONFIRM_MARKER_STRIP_RE, "")
    .replace(/\[\[confirm:[^\]]*$/, "")
    .trim();
}

function parseConfirmRequest(text: string): { summary: string } | null {
  if (!text) return null;
  const marked = text.match(CONFIRM_MARKER_RE);
  if (marked) return { summary: (marked[1] || "").trim() };
  if (CONFIRM_FALLBACK_RE.test(text)) return { summary: "" };
  return null;
}

type ToolPartInfo = {
  toolName: string;
  action: string;
  isWrite: boolean;
  entity: string;
  actionLabel: string;
  itemCount: number;
};

function parseToolPart(part: any): ToolPartInfo {
  const type = typeof part?.type === "string" ? part.type : "";
  const toolName = type.startsWith("tool-") ? type.slice(5) : type;
  const input = part?.input ?? {};
  const action = typeof input?.action === "string" ? input.action : "";
  const itemCount = Array.isArray(input?.items)
    ? input.items.length
    : Array.isArray(input?.steelAssemblyId)
      ? input.steelAssemblyId.length
      : 0;
  return {
    toolName,
    action,
    isWrite: WRITE_ACTIONS.has(action),
    entity: TOOL_ENTITY_LABEL[toolName] ?? toolName,
    actionLabel: TOOL_ACTION_LABEL[action] ?? action,
    itemCount,
  };
}

function summarizeToolOutput(output: any) {
  if (
    Array.isArray(output) &&
    output.some((r) => r && typeof r === "object" && "success" in r)
  ) {
    const failed = output.filter((r) => r?.success === false);
    return {
      batch: true,
      total: output.length,
      ok: output.length - failed.length,
      errors: failed.map((r) =>
        typeof r?.error === "string" ? r.error : JSON.stringify(r?.error),
      ),
    };
  }
  return { batch: false, total: 1, ok: 1, errors: [] as string[] };
}

function collectWriteToolCallIds(messages: any[]): string[] {
  const ids: string[] = [];
  for (const m of messages ?? []) {
    if (!Array.isArray(m?.parts)) continue;
    for (const part of m.parts) {
      if (typeof part?.type !== "string" || !part.type.startsWith("tool-")) {
        continue;
      }
      if (!parseToolPart(part).isWrite) continue;
      ids.push(part.toolCallId ?? `${m.id}:${part.type}`);
    }
  }
  return ids;
}

const NODE_TYPE_LABEL: Record<string, string> = {
  building: "建築",
  zone: "分區",
  batch: "分批",
  package: "構件包",
};

function nodeTypeLabel(type?: string) {
  if (!type) return "";
  return NODE_TYPE_LABEL[type] ?? type;
}

type ProjectNodeRow = {
  id: string;
  name?: string | null;
  nodeType?: string | null;
  parentNodeId?: string | null;
};

const NODE_TREE_TTL_MS = 15000;
const NODE_TREE_MAX_NODES = 400;

function buildNodeTreeLines(rows: ProjectNodeRow[]): string[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const childrenOf = new Map<string, ProjectNodeRow[]>();
  const roots: ProjectNodeRow[] = [];
  for (const row of rows) {
    const parentId = row.parentNodeId;
    if (parentId && parentId !== row.id && byId.has(parentId)) {
      const list = childrenOf.get(parentId) ?? [];
      list.push(row);
      childrenOf.set(parentId, list);
    } else {
      roots.push(row);
    }
  }

  const lines: string[] = [];
  const visited = new Set<string>();
  const walk = (row: ProjectNodeRow, depth: number) => {
    if (visited.has(row.id)) return;
    visited.add(row.id);
    const children = childrenOf.get(row.id) ?? [];
    const typeText = nodeTypeLabel(row.nodeType || "") || row.nodeType || "未知";
    const childText = children.length
      ? `子節點 ${children.length} 個：${children
          .map((c) => c.name || "(未命名)")
          .join("、")}`
      : "無子節點";
    lines.push(
      `${"  ".repeat(depth)}- ${row.name || "(未命名)"}（${typeText}｜ID:${row.id}）｜${childText}`,
    );
    for (const child of children) walk(child, depth + 1);
  };
  for (const root of roots) walk(root, 0);
  for (const row of rows) walk(row, 0);
  return lines;
}

// 節點結構直接讀 projectNode 資料表，避免模型改用 project.productionPlan（前端畫布快照，會殘留已刪除的節點）。
export async function fetchProjectNodeTreeText(projectId: string): Promise<string> {
  try {
    const res: any = await orpcClient.projectNode.list({
      filter: { projectId },
    });
    const rows: ProjectNodeRow[] = (Array.isArray(res) ? res : [])
      .slice(0, NODE_TREE_MAX_NODES)
      .map((row: any) => ({
        id: String(row?.id ?? ""),
        name: row?.name ?? null,
        nodeType: row?.nodeType ?? null,
        parentNodeId: row?.parentNodeId ?? null,
      }))
      .filter((row: ProjectNodeRow) => row.id);

    const rules = [
      "節點結構規則：",
      "1. 上面是此專案「全部」節點的完整清單與父子關係，未出現在上面的節點一律視為不存在（多半已被刪除）。",
      "2. project.productionPlan 只是前端畫布的快照，可能殘留已刪除的節點與過期的 childNodes；回答節點、父子關係、子節點數量時一律不得引用它。",
      "3. 工具回傳資料若與上面結構衝突，以上面結構為準；查不到就直接說查無此節點，不要用 ID 臆測名稱或類型。",
      "4. 只是要回答節點名稱、類型、子節點有哪些／幾個時，直接用上面的結構作答，不必再呼叫工具查詢節點。",
    ].join("\n");

    if (rows.length === 0) {
      return `專案節點結構（權威資料，取自 projectNode 資料表）：此專案目前沒有任何節點。\n${rules}`;
    }

    return [
      `專案節點結構（權威資料，取自 projectNode 資料表，共 ${rows.length} 個節點）：`,
      ...buildNodeTreeLines(rows),
      rules,
    ].join("\n");
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug("[ChatBot] fetchProjectNodeTreeText failed", e);
    return "";
  }
}

export function buildContextBlock(ctx?: ChatContextInfo, nodeTree?: string): string {
  if (!ctx) return "";
  const lines: string[] = [];
  if (ctx.projectId) {
    lines.push(
      `目前專案：${ctx.projectName || "（未命名）"}（專案ID：${ctx.projectId}）`,
    );
  }
  if (ctx.node) {
    const typeText = nodeTypeLabel(ctx.node.type);
    lines.push(
      `目前選取節點：${ctx.node.name}${typeText ? `（${typeText}）` : ""}（節點ID：${ctx.node.id}）`,
    );
    lines.push(
      "當使用者說「這個節點」「此節點」時，即是指上面這個節點，請直接以它為對象作答。",
    );
  } else {
    lines.push("目前未選取任何節點，請以整個專案為範圍作答。");
  }
  if (nodeTree) {
    lines.push(`${TREE_OPEN}\n${nodeTree}\n${TREE_CLOSE}`);
  }
  if (lines.length === 0) return "";
  return `${CONTEXT_OPEN}\n${lines.join("\n")}\n${CONTEXT_CLOSE}\n`;
}

function stripContextBlock(text: string): string {
  if (typeof text !== "string") return text;
  return text.replace(MODE_STRIP_RE, "").replace(CONTEXT_STRIP_RE, "");
}

// 歷史訊息裡的節點結構可能已過期，送回後端前只保留最後一則使用者訊息的版本。
export function stripStaleNodeTrees(messages: any[], keepLastUser = true): any[] {
  if (!Array.isArray(messages)) return messages;
  let lastUserIdx = -1;
  if (keepLastUser) {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === "user") {
        lastUserIdx = i;
        break;
      }
    }
  }
  return messages.map((m: any, i: number) => {
    if (i === lastUserIdx || m?.role !== "user" || !Array.isArray(m?.parts)) {
      return m;
    }
    let changed = false;
    const parts = m.parts.map((p: any) => {
      if (p?.type !== "text" || typeof p.text !== "string") return p;
      if (!p.text.includes(TREE_OPEN)) return p;
      changed = true;
      return { ...p, text: p.text.replace(TREE_STRIP_RE, "") };
    });
    return changed ? { ...m, parts } : m;
  });
}

type PageContext = {
  key: string;
  label: string;
  match: RegExp;
  suggestions: string[];
  allowedEntities?: EntityType[];
};

const DEFAULT_SUGGESTIONS = [
  "公司資訊",
  "員工管理",
  "部門管理",
  "新增部門要填寫哪些資訊？",
  "應付帳款",
  "銀行餘額多少？",
  "採購單（草稿）",
  "合作方列表",
  "帶我去倉庫與儲位管理",
];

const PAGE_CONTEXTS: PageContext[] = [
  {
    key: "product",
    label: "商品管理",
    match: /^\/hrMainPage\/(product|addProduct|editProduct)/i,
    suggestions: [
      "商品有幾筆？",
      "找名稱含「鋼板」的商品",
      "新增商品要去哪裡？",
    ],
    allowedEntities: ["product"],
  },
  {
    key: "warehouse",
    label: "倉庫與儲位管理",
    match: /^\/hrMainPage\/(warehouse|addWarehouse|editWarehouse)/i,
    suggestions: ["倉庫列表", "有幾個倉庫？", "各倉庫有哪些儲位？"],
    allowedEntities: ["warehouse"],
  },
  {
    key: "stockHistory",
    label: "進出貨歷程",
    match: /^\/hrMainPage\/stockHistory/i,
    suggestions: ["最近的入庫單", "最近的領料單", "目前庫存有哪些商品？"],
    allowedEntities: ["stockReceipt", "stockRequest"],
  },
  {
    key: "stockReceipt",
    label: "入庫單管理",
    match:
      /^\/hrMainPage\/(inventoryCreated|inventoryDone|addInventoryCreated|addInventoryDone|editInventoryCreated|editInventoryDone)/i,
    suggestions: ["已完成的入庫單", "製作中的入庫單", "入庫單有幾筆？"],
    allowedEntities: ["stockReceipt"],
  },
  {
    key: "stockRequest",
    label: "領料單管理",
    match: /^\/hrMainPage\/(stockRequest|addStockRequest|editStockRequest)/i,
    suggestions: ["已建立的領料單", "已完成的領料單", "領料單有幾筆？"],
    allowedEntities: ["stockRequest"],
  },
  {
    key: "purchaseOrder",
    label: "採購單管理",
    match: /^\/hrMainPage\/(purchaseOrder|addPurchaseOrder|editPurchaseOrder)/i,
    suggestions: ["採購單（草稿）", "審核中的採購單", "已完成的採購單"],
    allowedEntities: ["purchaseOrder"],
  },
  {
    key: "partner",
    label: "合作廠商",
    match: /^\/hrMainPage\/coop/i,
    suggestions: ["合作方列表", "我要新增合作方", "審核中的合作方", "停用的合作方"],
    allowedEntities: ["partner"],
  },
  {
    key: "employee",
    label: "員工管理",
    match: /^\/hrMainPage\/(employee|addEmployee|editEmployee)/i,
    suggestions: [
      "員工列表",
      "我要新增員工",
      "修改員工 王小明 的資料",
      "讓員工 王小明 離職",
    ],
    allowedEntities: ["employee"],
  },
  {
    key: "department",
    label: "部門管理",
    match: /^\/hrMainPage\/(department|addDepartment|editDepartment)/i,
    suggestions: ["部門列表", "新增部門要填寫哪些資訊？"],
    allowedEntities: ["department"],
  },
  {
    key: "company",
    label: "公司資訊",
    match: /^\/hrMainPage\/(companyInfo|editCompanyInfo)/i,
    suggestions: ["公司資訊", "公司的統一編號是？"],
    allowedEntities: ["company"],
  },
  {
    key: "finance",
    label: "財務",
    match:
      /^\/hrMainPage\/(accountPayable|editAccountPayable|accountReceivable|editAccountReceivable)/i,
    suggestions: [
      "應收帳款",
      "應付帳款",
      "未收金額最多的是哪筆？",
      "未付金額最多的是哪筆？",
    ],
    allowedEntities: ["accountReceivable", "accountPayable"],
  },
  {
    key: "bank",
    label: "銀行管理",
    match: /^\/hrMainPage\/bank/i,
    suggestions: ["銀行餘額多少？", "銀行列表"],
    allowedEntities: ["bank"],
  },
  {
    key: "productionPlan",
    label: "生產計畫",
    match: /^\/productionPlan/i,
    suggestions: [
      "這個專案的資料",
      "這個節點是什麼？",
      "這個節點底下有哪些子節點？",
      "專案列表",
    ],
    allowedEntities: [
      "project",
      "projectNode",
      "workItem",
      "scanHistory",
      "designBom",
      "steelAssembly",
    ],
  },
  {
    key: "project",
    label: "專案管理",
    match:
      /^\/hrMainPage\/(projectQuotation|editQuotation|addProjectQuotation)/i,
    suggestions: ["專案列表", "專案有幾個？", "案件節點"],
    allowedEntities: [
      "project",
      "projectNode",
      "workItem",
      "scanHistory",
      "designBom",
      "steelAssembly",
      "materialBom",
      "steelBoltBom",
    ],
  },
  {
    key: "bom",
    label: "BOM 管理",
    match:
      /^\/hrMainPage\/(designBOM|addDesignBOM|steelAssemblyBOM|addSteelAssemblyBOM|steelBoltBOM|addSteelBoltBOM)/i,
    suggestions: ["專案列表", "構件有幾筆？", "案件節點"],
    allowedEntities: [
      "project",
      "projectNode",
      "designBom",
      "steelAssembly",
      "materialBom",
      "steelBoltBom",
    ],
  },
];

const GENERAL_PAGE_CONTEXT: PageContext = {
  key: "general",
  label: "全部功能",
  match: /.*/,
  suggestions: DEFAULT_SUGGESTIONS,
};

function getPageContext(pathname: string): PageContext {
  return (
    PAGE_CONTEXTS.find((c) => c.match.test(pathname)) ?? GENERAL_PAGE_CONTEXT
  );
}

export function isAiAssistantPath(pathname: string): boolean {
  return BACKEND_PAGE_KEYS.has(getPageContext(pathname).key);
}

const getCompanyId = () => localStorage.getItem("companyId") ?? "";

function getNested(obj: any, path: string): any {
  if (!obj) return undefined;
  const parts = path.split(".");
  let cur: any = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    if (Array.isArray(cur)) {
      cur = cur.map((c) => c?.[p]).filter(Boolean);
      continue;
    }
    cur =
      cur.attributes?.[p] ??
      cur.data?.attributes?.[p] ??
      cur.data?.[p] ??
      cur[p];
  }
  return cur;
}

function flattenList(payload: any): Record<string, any>[] {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.data?.data)
        ? payload.data.data
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
  if (typeof window !== "undefined") {
    // eslint-disable-next-line no-console
    console.debug(
      "[ChatBot] flattenList payload→",
      payload,
      "rows:",
      list.length,
    );
  }
  return list.map((item: any) => {
    const attrs = item?.attributes ?? item ?? {};
    return {
      ...attrs,
      id: String(item?.documentId ?? attrs?.documentId ?? item?.id ?? ""),
    };
  });
}

function filterRows(rows: any[], args: any): any[] {
  let r = rows;
  if (args.status) {
    const s = String(args.status).toLowerCase();
    r = r.filter(
      (x) =>
        String(x.status ?? "").toLowerCase() === s ||
        String(x.orderStatus ?? "").toLowerCase() === s,
    );
  }
  if (args.name) {
    const q = String(args.name).toLowerCase();
    r = r.filter((x) => {
      const candidates = [x.name, x.code, x.orderNumber, x.partName, x.componentCode, x.steelAssemblyCode, x.steelAssemblyId];
      return candidates.some(
        (c) => typeof c === "string" && c.toLowerCase().includes(q),
      );
    });
  }
  if (args.partner) {
    const q = String(args.partner).toLowerCase();
    r = r.filter((x) => {
      const p = x.partner?.name ?? x.partner ?? "";
      return typeof p === "string" && p.toLowerCase().includes(q);
    });
  }
  if (args.nodeType) {
    r = r.filter((x) => x.nodeType === args.nodeType);
  }
  return r;
}

function flattenDetail(payload: any): Record<string, any> {
  const item = payload?.data ?? payload ?? {};
  const attrs = item.attributes ?? item;
  return {
    ...attrs,
    id: String(item.documentId ?? attrs.documentId ?? item.id ?? ""),
  };
}

// productionPlan 是前端畫布快照、會與 projectNode 資料表不同步，不能餵給模型。
export function stripProductionPlan(record: any): any {
  if (!record || typeof record !== "object") return record;
  const { productionPlan, ...rest } = record as any;
  return rest;
}

export function sanitizeProjectNodeRecord(node: any): any {
  if (!node || typeof node !== "object") return node;
  const { project, parentNode, childNode, ...rest } = node as any;
  return {
    ...rest,
    project: project ? { id: project.id, name: project.name } : null,
    parentNode: parentNode
      ? {
          id: parentNode.id,
          name: parentNode.name,
          nodeType: parentNode.nodeType,
        }
      : null,
    childNode: Array.isArray(childNode)
      ? childNode.map((child: any) => ({
          id: child?.id,
          name: child?.name,
          nodeType: child?.nodeType,
        }))
      : [],
  };
}

const ENTITY_CONFIG: Record<
  EntityType,
  {
    label: string;
    listFn: (args: any) => Promise<any>;
    getFn: (id: string, args?: any) => Promise<any>;
    columns: ColumnDef[];
    detailFields: { label: string; key: string; format?: ColumnDef["format"] }[];
    itemPath?: (id: string) => string;
    listPath?: string;
    filterDescription: string;
  }
> = {
  company: {
    label: "公司資訊",
    listFn: () => orpcClient.company.list({}),
    getFn: () => orpcClient.company.read({}),
    columns: [
      { title: "公司名稱", key: "name" },
      { title: "統一編號", key: "businessIdNumber" },
      { title: "負責人", key: "representative" },
    ],
    detailFields: [
      { label: "公司名稱", key: "name" },
      { label: "公司簡稱", key: "companyAbbreviation" },
      { label: "統一編號", key: "businessIdNumber" },
      { label: "負責人", key: "representative" },
      { label: "營業項目", key: "business" },
      { label: "資本額", key: "capital" },
      { label: "Email", key: "email" },
      { label: "市內電話", key: "localPhone" },
      { label: "傳真", key: "fax" },
      { label: "聯絡人", key: "contact" },
      { label: "電話", key: "phone" },
      { label: "地址", key: "fullAddress" },
      { label: "縣市", key: "city" },
      { label: "行政區", key: "district" },
      { label: "郵遞區號", key: "postalCode" },
      { label: "網站", key: "website" },
      { label: "發票抬頭", key: "invoiceTitle" },
    ],
    listPath: "/companyListPage",
    itemPath: () => "/hrMainPage/companyInfo",
    filterDescription: "name(client-side, 部分比對)",
  },
  project: {
    label: "案件",
    listFn: async () => {
      const res: any = await orpcClient.project.list({});
      return Array.isArray(res) ? res.map(stripProductionPlan) : res;
    },
    getFn: async (id) =>
      stripProductionPlan(await single(orpcClient.project.read([{ id }]))),
    columns: [
      { title: "名稱", key: "name" },
      { title: "日期", key: "date", format: "date" },
      { title: "合作廠商", key: "partner.name" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "日期", key: "date", format: "date" },
      { label: "合作廠商", key: "partner.name" },
      { label: "描述", key: "description" },
    ],
    itemPath: (id) => `/productionPlan/${id}`,
    listPath: "/hrMainPage/projectQuotation",
    filterDescription: "name、partner、date（client-side, 部分比對）",
  },
  projectNode: {
    label: "案件節點",
    listFn: async (args) => {
      if (!args.projectId) return [];
      const res: any = await orpcClient.projectNode.list({
        filter: { projectId: args.projectId },
      });
      return Array.isArray(res) ? res.map(sanitizeProjectNodeRecord) : res;
    },
    getFn: async (id, args) => {
      if (!args?.projectId) return null;
      return sanitizeProjectNodeRecord(
        await single(orpcClient.projectNode.read([{ id }])),
      );
    },
    columns: [
      { title: "節點類型", key: "nodeType" },
      { title: "名稱", key: "name" },
      { title: "所屬案件", key: "project.name" },
    ],
    detailFields: [
      { label: "節點類型", key: "nodeType" },
      { label: "名稱", key: "name" },
      { label: "所屬案件", key: "project.name" },
      { label: "父節點", key: "parentNode.name" },
      { label: "子節點", key: "childNode.name" },
    ],
    filterDescription:
      "projectId 必填（先用 project 清單找出案件 documentId 再帶入）；name、nodeType（client-side）。" +
      "父子關係只看 parentNodeId／childNode，不可引用 project.productionPlan（畫布快照，會有已刪除的殘留節點）",
  },
  employee: {
    label: "員工",
    listFn: () => orpcClient.employee.list({}),
    getFn: (id) => single(orpcClient.employee.read([{ id }])),
    columns: [
      { title: "姓名", key: "name" },
      { title: "電話", key: "phone" },
      { title: "部門", key: "department.name" },
    ],
    detailFields: [
      { label: "姓名", key: "name" },
      { label: "電話", key: "phone" },
      { label: "Email", key: "user.email" },
      { label: "部門", key: "department.name" },
      { label: "性別", key: "gender" },
      { label: "生日", key: "birthday", format: "date" },
      { label: "血型", key: "bloodType" },
      { label: "身分證字號", key: "nationalIdNo" },
      { label: "機車駕照", key: "motorbikeLicense" },
      { label: "汽車駕照", key: "carLicense" },
      { label: "地址", key: "address" },
      { label: "緊急聯絡人", key: "emergencyContact" },
      { label: "緊急聯絡人關係", key: "emergencyContactRelationship" },
      { label: "緊急聯絡人電話", key: "emergencyContactPhoneNumber" },
      { label: "員工標籤", key: "employeeTag.name" },
      { label: "備註", key: "note" },
      { label: "狀態", key: "status" },
    ],
    itemPath: (id) => `/hrMainPage/employee/${id}`,
    listPath: "/hrMainPage/employee",
    filterDescription: "name（client-side, 部分比對）",
  },
  department: {
    label: "部門",
    listFn: async () => {
      const cid = getCompanyId();
      if (!cid) return [];
      const result: any = await apiUserGetCompany({ id: cid });
      return result?.data?.departments ?? [];
    },
    getFn: async (id) => {
      const cid = getCompanyId();
      if (!cid) return null;
      const result: any = await apiUserGetCompany({ id: cid });
      const list = result?.data?.departments ?? [];
      return (
        list.find(
          (d: any) => d.id === id || d.documentId === id,
        ) ?? null
      );
    },
    columns: [
      { title: "名稱", key: "name" },
      { title: "代號", key: "code" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "代號", key: "code" },
      { label: "描述", key: "description" },
    ],
    itemPath: (id) => `/hrMainPage/department/${id}`,
    listPath: "/hrMainPage/department",
    filterDescription: "name（client-side）。資料來自目前公司的 departments",
  },
  partner: {
    label: "合作廠商",
    listFn: () => orpcClient.partner.list({}),
    getFn: (id) => single(orpcClient.partner.read([{ id }])),
    columns: [
      { title: "名稱", key: "name" },
      { title: "統編", key: "businessIdNumber" },
      { title: "電話", key: "telephone" },
      { title: "狀態", key: "status", format: "tag" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "簡稱", key: "abbreviation" },
      { label: "統一編號", key: "businessIdNumber" },
      { label: "編號", key: "serialNumber" },
      { label: "營業項目", key: "business" },
      { label: "電話", key: "telephone" },
      { label: "傳真", key: "fax" },
      { label: "發票種類", key: "invoiceType" },
      { label: "營業登記地址", key: "businessAddress", format: "address" },
      { label: "發票地址", key: "invoiceAddress", format: "address" },
      { label: "送貨地址", key: "shippingAddress", format: "address" },
      { label: "聯絡人", key: "contacts", format: "contacts" },
      { label: "狀態", key: "status" },
      { label: "申請人", key: "applicant.name" },
      { label: "申請日期", key: "applicationDate", format: "date" },
      { label: "審核人", key: "reviewer.name" },
      { label: "審核日期", key: "reviewDate", format: "date" },
      { label: "停用原因", key: "disableReason" },
    ],
    itemPath: (id) => `/hrMainPage/coop/${id}`,
    listPath: "/hrMainPage/coop",
    filterDescription: "name（client-side, 部分比對）",
  },
  product: {
    label: "商品",
    listFn: () => orpcClient.product.list({}),
    getFn: (id) => single(orpcClient.product.read([{ id }])),
    columns: [
      { title: "名稱", key: "name" },
      { title: "單位", key: "unit" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "單位", key: "unit" },
      { label: "描述", key: "description" },
    ],
    itemPath: (id) => `/hrMainPage/product/${id}`,
    listPath: "/hrMainPage/product",
    filterDescription: "name（client-side, 部分比對）",
  },
  purchaseOrder: {
    label: "採購單",
    listFn: () => orpcClient.purchaseOrder.list({}),
    getFn: (id) => single(orpcClient.purchaseOrder.read([{ id }])),
    columns: [
      { title: "編號", key: "orderNumber" },
      { title: "日期", key: "date", format: "date" },
      { title: "供應商", key: "partner.name" },
      { title: "狀態", key: "status", format: "tag" },
    ],
    detailFields: [
      { label: "編號", key: "orderNumber" },
      { label: "日期", key: "date", format: "date" },
      { label: "供應商", key: "partner.name" },
      { label: "狀態", key: "status" },
      { label: "備註", key: "note" },
    ],
    itemPath: (id) => `/hrMainPage/purchaseOrder/${id}`,
    filterDescription:
      "status（draft｜underReview｜approved｜completed）、name（client-side）",
  },
  accountPayable: {
    label: "應付帳款",
    listFn: () => orpcClient.accountsPayable.list({}),
    getFn: (id) => single(orpcClient.accountsPayable.read([{ id }])),
    columns: [
      { title: "名稱", key: "name" },
      { title: "供應商", key: "partner.name" },
      { title: "應付金額", key: "totalAmount", format: "money" },
      { title: "狀態", key: "status", format: "tag" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "供應商", key: "partner.name" },
      { label: "應付金額", key: "totalAmount", format: "money" },
      { label: "未付金額", key: "unpaidAmount", format: "money" },
      { label: "已付金額", key: "paidAmount", format: "money" },
      { label: "狀態", key: "status" },
      { label: "備註", key: "remarks" },
    ],
    itemPath: (id) => `/hrMainPage/editAccountPayable/${id}`,
    listPath: "/hrMainPage/accountPayable",
    filterDescription: "name、partner（client-side, 部分比對）",
  },
  accountReceivable: {
    label: "應收帳款",
    listFn: () => orpcClient.accountsReceivable.list({}),
    getFn: (id) => single(orpcClient.accountsReceivable.read([{ id }])),
    columns: [
      { title: "名稱", key: "name" },
      { title: "客戶", key: "partner.name" },
      { title: "應收金額", key: "totalAmount", format: "money" },
      { title: "狀態", key: "status", format: "tag" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "客戶", key: "partner.name" },
      { label: "應收金額", key: "totalAmount", format: "money" },
      { label: "未收金額", key: "unpaidAmount", format: "money" },
      { label: "已收金額", key: "paidAmount", format: "money" },
      { label: "狀態", key: "status" },
      { label: "備註", key: "remarks" },
    ],
    itemPath: (id) => `/hrMainPage/editAccountReceivable/${id}`,
    listPath: "/hrMainPage/accountReceivable",
    filterDescription: "name、partner（client-side, 部分比對）",
  },
  stockRequest: {
    label: "領料單",
    listFn: () => orpcClient.stockRequest.list({}),
    getFn: (id) => single(orpcClient.stockRequest.read([{ id }])),
    columns: [
      { title: "名稱", key: "name" },
      { title: "建立時間", key: "createdAt", format: "datetime" },
      { title: "狀態", key: "status", format: "tag" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "建立時間", key: "createdAt", format: "datetime" },
      { label: "狀態", key: "status" },
      { label: "備註", key: "remarks" },
    ],
    itemPath: (id) => `/hrMainPage/stockRequestCreated/${id}`,
    listPath: "/hrMainPage/stockRequestCreated",
    filterDescription: "status（done｜created…）、name（client-side）",
  },
  stockReceipt: {
    label: "入庫單",
    listFn: () => orpcClient.stockReceipt.list({}),
    getFn: (id) => single(orpcClient.stockReceipt.read([{ id }])),
    columns: [
      { title: "名稱", key: "name" },
      { title: "建立時間", key: "createdAt", format: "datetime" },
      { title: "狀態", key: "status", format: "tag" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "建立時間", key: "createdAt", format: "datetime" },
      { label: "狀態", key: "status" },
      { label: "送貨人員", key: "deliveryPerson" },
      { label: "送貨車牌", key: "licensePlateNumber" },
      { label: "備註", key: "remarks" },
    ],
    itemPath: (id) => `/hrMainPage/inventoryDone/${id}`,
    listPath: "/hrMainPage/inventoryDone",
    filterDescription: "status（done｜created…）、name（client-side）",
  },
  warehouse: {
    label: "倉庫",
    listFn: () => orpcClient.warehouse.list({}),
    getFn: (id) => single(orpcClient.warehouse.read([{ id }])),
    columns: [
      { title: "名稱", key: "name" },
      { title: "地址", key: "address" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "地址", key: "address" },
      { label: "描述", key: "description" },
    ],
    itemPath: (id) => `/hrMainPage/warehouse/${id}`,
    listPath: "/hrMainPage/warehouse",
    filterDescription: "name（client-side）",
  },
  bank: {
    label: "銀行",
    listFn: () => orpcClient.bank.list({}),
    getFn: async () => null,
    columns: [
      { title: "銀行名稱", key: "name" },
      { title: "餘額", key: "balance", format: "money" },
    ],
    detailFields: [
      { label: "銀行名稱", key: "name" },
      { label: "餘額", key: "balance", format: "money" },
      { label: "備註", key: "remarks" },
    ],
    listPath: "/hrMainPage/bank",
    filterDescription: "name（client-side）",
  },
  materialBom: {
    label: "材料 BOM",
    listFn: (args) =>
      args.projectId
        ? orpcClient.materialBom.list({
            filter: { projectId: args.projectId },
          })
        : Promise.resolve([]),
    getFn: async () => null,
    columns: [
      { title: "名稱", key: "name" },
      { title: "編號", key: "code" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "編號", key: "code" },
    ],
    filterDescription: "name（client-side）",
  },
  steelBoltBom: {
    label: "螺絲 BOM",
    listFn: (args) =>
      args.projectId
        ? orpcClient.steelBoltBom.list({
            filter: { projectId: args.projectId },
          })
        : Promise.resolve([]),
    getFn: async () => null,
    columns: [
      { title: "零件編號", key: "partNumber" },
      { title: "零件名稱", key: "partName" },
      { title: "規格", key: "specification" },
    ],
    detailFields: [
      { label: "零件編號", key: "partNumber" },
      { label: "零件名稱", key: "partName" },
      { label: "規格", key: "specification" },
      { label: "材質", key: "material" },
      { label: "數量", key: "quantity" },
    ],
    filterDescription: "partName、partNumber（client-side）",
  },
  steelAssembly: {
    label: "鋼構件",
    listFn: (args) =>
      args.buildingId
        ? orpcClient.steelAssembly.list({
            filter: { buildingId: args.buildingId },
          })
        : Promise.resolve([]),
    getFn: async () => null,
    columns: [
      { title: "構件ID", key: "componentId" },
      { title: "構件編號", key: "componentCode" },
      { title: "廣達ID", key: "guandaId" },
    ],
    detailFields: [
      { label: "構件ID", key: "componentId" },
      { label: "構件編號", key: "componentCode" },
      { label: "廣達ID", key: "guandaId" },
      { label: "安裝位置", key: "installationLocation" },
      { label: "斷面規格", key: "metricSizeMark" },
    ],
    filterDescription:
      "buildingId 必填（從 project.read → projectNode 取 building 的 id）",
  },
  designBom: {
    label: "設計BOM",
    listFn: (args) =>
      args.projectId
        ? orpcClient.steelAssembly.list({
            filter: { projectId: args.projectId },
          })
        : args.buildingId
          ? orpcClient.steelAssembly.list({
              filter: { buildingId: args.buildingId },
            })
          : Promise.resolve([]),
    getFn: async () => null,
    columns: [
      { title: "構件ID", key: "steelAssemblyId" },
      { title: "構件編號", key: "steelAssemblyCode" },
      { title: "廣達ID", key: "guandaId" },
      { title: "安裝位置", key: "installationLocation" },
      { title: "斷面規格", key: "metricSizeMark" },
    ],
    detailFields: [
      { label: "構件ID", key: "steelAssemblyId" },
      { label: "構件編號", key: "steelAssemblyCode" },
      { label: "廣達ID", key: "guandaId" },
      { label: "安裝位置", key: "installationLocation" },
      { label: "安裝高程", key: "installationElevation" },
      { label: "斷面規格", key: "metricSizeMark" },
      { label: "圖面名稱", key: "drawingName" },
      { label: "總長度", key: "totalLength" },
      { label: "總寬度", key: "totalWidth" },
      { label: "總高度", key: "totalHeight" },
      { label: "總重量", key: "totalWeight" },
      { label: "總面積", key: "totalArea" },
      { label: "所屬案件", key: "project.name" },
      { label: "備註", key: "note" },
    ],
    filterDescription:
      "projectId（查整個專案的設計BOM，最常用：先用 project 清單找出案件 documentId 再帶入）；或 buildingId（單一建築節點）。也支援 name（client-side，比對構件編號／構件ID）",
  },
  workItem: {
    label: "工項",
    listFn: (args) =>
      args.projectNodeId
        ? orpcClient.workItem.list({
            filter: { projectNodeId: args.projectNodeId },
          })
        : Promise.resolve([]),
    getFn: async () => null,
    columns: [
      { title: "名稱", key: "name" },
      { title: "掃描模式", key: "scanMode" },
    ],
    detailFields: [
      { label: "名稱", key: "name" },
      { label: "掃描模式", key: "scanMode" },
    ],
    filterDescription: "projectNodeId 必填（工項掛在節點下，沒帶就查不到）",
  },
  scanHistory: {
    label: "掃描加工紀錄",
    listFn: (args) =>
      args.workItemId
        ? orpcClient.scanHistory.list({
            filter: { workItemId: args.workItemId },
          })
        : Promise.resolve([]),
    getFn: async () => null,
    columns: [
      { title: "構件", key: "steelAssembly.name" },
      { title: "工項", key: "workItem.name" },
      { title: "員工", key: "employee.name" },
      { title: "時間", key: "scannedAt", format: "datetime" },
    ],
    detailFields: [
      { label: "構件", key: "steelAssembly.name" },
      { label: "工項", key: "workItem.name" },
      { label: "員工", key: "employee.name" },
      { label: "時間", key: "scannedAt", format: "datetime" },
      { label: "狀態", key: "status" },
    ],
    filterDescription: "workItemId、employeeId、steelAssemblyId",
  },
};

const ENTITY_TYPES = Object.keys(ENTITY_CONFIG) as EntityType[];

function getAllowedEntities(pageContext: PageContext): EntityType[] {
  return pageContext.allowedEntities ?? ENTITY_TYPES;
}

function isGeneralContext(pageContext: PageContext): boolean {
  return !pageContext.allowedEntities;
}

type FormType = "department" | "employee" | "partner";

type FormFieldDef = {
  key: string;
  label: string;
  type:
    | "text"
    | "textarea"
    | "employeeMulti"
    | "select"
    | "date"
    | "section";
  required?: boolean;
  placeholder?: string;
  hint?: string;
  options?: { label: string; value: string }[];
};

const FORM_CONFIG: Record<
  FormType,
  {
    title: string;
    description: string;
    fields: FormFieldDef[];
    addPath: string;
    listPath?: string;
    listLabel?: string;
    mode?: "submit" | "info";
    submit?: (values: Record<string, any>) => Promise<any>;
    isSuccess?: (res: any) => boolean;
  }
> = {
  department: {
    title: "新增部門",
    description: "填寫以下資訊即可建立部門",
    fields: [
      { key: "name", label: "部門名稱", type: "text", required: true },
      { key: "code", label: "部門代號", type: "text" },
      { key: "workContent", label: "部門描述", type: "textarea" },
      { key: "leaders", label: "部門主管", type: "employeeMulti" },
      { key: "members", label: "部門成員", type: "employeeMulti" },
    ],
    addPath: "/hrMainPage/addDepartment",
    listPath: "/hrMainPage/department",
    listLabel: "查看部門列表",
    isSuccess: (res) => !!res?.data,
    submit: async (values) => {
      const data = {
        name: values.name ?? "",
        code: values.code ?? "",
        workContent: values.workContent ?? "",
        leaders: Array.isArray(values.leaders) ? values.leaders : [],
        members: Array.isArray(values.members) ? values.members : [],
        company: getCompanyId(),
      };
      return apiUserPostDepartment({ data });
    },
  },
  employee: {
    title: "新增員工",
    description:
      "透過系統已存在的使用者 Email 建立員工，並填寫基本資料後直接送出",
    addPath: "/hrMainPage/addEmployee",
    listPath: "/hrMainPage/employees",
    listLabel: "查看員工列表",
    isSuccess: (res) => !!res?.id,
    fields: [
      {
        key: "email",
        label: "Email / 帳號",
        type: "text",
        required: true,
        hint: "必須是系統已存在的使用者 Email",
      },
      { key: "name", label: "姓名", type: "text", required: true },
      {
        key: "gender",
        label: "生理性別",
        type: "select",
        options: [
          { label: "男生", value: "male" },
          { label: "女生", value: "female" },
        ],
      },
      { key: "birthday", label: "生日", type: "date" },
      { key: "bloodType", label: "血型", type: "text" },
      { key: "nationalIdNo", label: "身分證字號", type: "text" },
      {
        key: "motorbikeLicense",
        label: "機車駕照",
        type: "select",
        options: [
          { label: "有", value: "true" },
          { label: "沒有", value: "false" },
        ],
      },
      {
        key: "carLicense",
        label: "汽車駕照",
        type: "select",
        options: [
          { label: "有", value: "true" },
          { label: "沒有", value: "false" },
        ],
      },
      { key: "city", label: "縣市", type: "text" },
      { key: "district", label: "行政區", type: "text" },
      { key: "postalCode", label: "郵遞區號", type: "text" },
      { key: "addressLine", label: "地址", type: "text" },
      { key: "emergencyContact", label: "緊急聯絡人", type: "text" },
      {
        key: "emergencyContactRelationship",
        label: "緊急聯絡人關係",
        type: "text",
      },
      {
        key: "emergencyContactPhoneNumber",
        label: "緊急聯絡人電話",
        type: "text",
      },
      { key: "note", label: "備註", type: "textarea" },
    ],
    submit: async (values) => {
      const created: any = await orpcClient.employee.addMember({
        email: String(values.email ?? "").trim(),
      });
      if (!created?.id) {
        throw new Error(
          "新增成員失敗，請確認 Email 為系統已存在的使用者，且尚未加入此公司",
        );
      }
      const hasAddress =
        values.city &&
        values.district &&
        values.postalCode &&
        values.addressLine;
      const updateData: any = {
        id: created.id,
        name: values.name ?? "",
        gender: values.gender || undefined,
        bloodType: values.bloodType || undefined,
        nationalIdNo: values.nationalIdNo || undefined,
        motorbikeLicense: values.motorbikeLicense === "true",
        carLicense: values.carLicense === "true",
        address: hasAddress
          ? {
              city: values.city,
              district: values.district,
              postalCode: Number(values.postalCode),
              address: values.addressLine,
            }
          : null,
        emergencyContact: values.emergencyContact || undefined,
        emergencyContactRelationship:
          values.emergencyContactRelationship || undefined,
        emergencyContactPhoneNumber:
          values.emergencyContactPhoneNumber || undefined,
        note: values.note || undefined,
      };
      if (values.birthday) {
        updateData.birthday = new Date(values.birthday);
      }
      return single(orpcClient.employee.update([updateData]));
    },
  },
  partner: {
    title: "新增合作方",
    description: "填寫合作方基本資料後直接送出建立（標 * 為必填）",
    addPath: "/hrMainPage/addCoop",
    listPath: "/hrMainPage/coop",
    listLabel: "查看合作方列表",
    isSuccess: (res) => !!res?.id,
    fields: [
      { key: "sec_basic", label: "基本資訊", type: "section" },
      { key: "name", label: "公司名稱", type: "text", required: true },
      { key: "abbreviation", label: "簡稱", type: "text" },
      {
        key: "businessIdNumber",
        label: "統一編號",
        type: "text",
        required: true,
      },
      { key: "business", label: "營業項目", type: "textarea" },
      { key: "sec_contact", label: "聯絡資訊", type: "section" },
      { key: "telephone", label: "電話", type: "text" },
      { key: "fax", label: "傳真", type: "text" },
      { key: "invoiceType", label: "發票種類", type: "text", hint: "二聯／三聯" },
      { key: "contactName", label: "聯絡人姓名", type: "text" },
      { key: "contactDepartment", label: "聯絡人部門", type: "text" },
      { key: "contactTelephone", label: "聯絡人電話", type: "text" },
      { key: "contactEmail", label: "聯絡人 Email", type: "text" },
      { key: "sec_biz_addr", label: "營業登記地址", type: "section" },
      { key: "biz_city", label: "縣市", type: "text" },
      { key: "biz_district", label: "行政區", type: "text" },
      { key: "biz_postalCode", label: "郵遞區號", type: "text" },
      { key: "biz_address", label: "地址", type: "text" },
      { key: "sec_inv_addr", label: "發票地址（留空則同營業地址）", type: "section" },
      { key: "inv_city", label: "縣市", type: "text" },
      { key: "inv_district", label: "行政區", type: "text" },
      { key: "inv_postalCode", label: "郵遞區號", type: "text" },
      { key: "inv_address", label: "地址", type: "text" },
      {
        key: "sec_ship_addr",
        label: "送貨地址（留空則同營業地址）",
        type: "section",
      },
      { key: "ship_city", label: "縣市", type: "text" },
      { key: "ship_district", label: "行政區", type: "text" },
      { key: "ship_postalCode", label: "郵遞區號", type: "text" },
      { key: "ship_address", label: "地址", type: "text" },
    ],
    submit: async (values) => {
      const buildAddress = (prefix: string) => {
        const city = values[`${prefix}_city`];
        const district = values[`${prefix}_district`];
        const postalCode = values[`${prefix}_postalCode`];
        const address = values[`${prefix}_address`];
        if (city && district && postalCode && address) {
          return {
            city,
            district,
            postalCode: Number(postalCode),
            address,
          };
        }
        return undefined;
      };
      const businessAddress = buildAddress("biz");
      const invoiceAddress = buildAddress("inv") ?? businessAddress;
      const shippingAddress = buildAddress("ship") ?? businessAddress;
      const data: any = {
        name: values.name ?? "",
        abbreviation: values.abbreviation || undefined,
        businessIdNumber: values.businessIdNumber ?? "",
        business: values.business || undefined,
        telephone: values.telephone || undefined,
        fax: values.fax || undefined,
        invoiceType: values.invoiceType || undefined,
        contactName: values.contactName || undefined,
        contactDepartment: values.contactDepartment || undefined,
        contactTelephone: values.contactTelephone || undefined,
        contactEmail: values.contactEmail || undefined,
        businessAddress,
        invoiceAddress,
        shippingAddress,
      };
      return single(orpcClient.partner.create([data]));
    },
  },
};

const FORM_TYPES = Object.keys(FORM_CONFIG) as FormType[];

async function fetchCompanyEmployeeOptions(): Promise<
  { label: string; value: string }[]
> {
  const cid = getCompanyId();
  if (!cid) return [];
  const result: any = await apiUserGetCompany({ id: cid });
  const employees: any[] = result?.data?.employees ?? [];
  return employees
    .filter((e) => e?.documentId)
    .map((e) => ({
      label: `${e?.name ?? "(未命名)"}（ID: ${e?.id ?? "—"}）`,
      value: String(e.documentId),
    }));
}

const ROUTE_MAP = `
公開：/login, /signUp, /phoneVerification, /companyListPage, /addCompanyListPage
案件操作：/productionPlan/{id}
hrMainPage 子頁面（前面加 /hrMainPage/）：
- 公司：companyInfo, editCompanyInfo
- 員工：employee, employee/{id}, editEmployee/{id}, addEmployee
- 部門：department, department/{id}, editDepartment/{id}, addDepartment
- 合作廠商：coop, coop/{id}, editCoop/{id}, addCoop, coopPending, coopReviewed
- 產品：product, product/{id}, editProduct/{id}, addProduct
- 製作中盤點：inventoryCreated, inventoryCreated/{id}, editInventoryCreated/{id}, addInventoryCreated
- 完成盤點：inventoryDone, inventoryDone/{id}, editInventoryDone/{id}, addInventoryDone
- 領料申請：stockRequestCreated, stockRequestCreated/{id}, editStockRequestCreated/{id}, addStockRequestCreated
- 已領料：stockRequestDone, stockRequestDone/{id}, editStockRequestDone/{id}, addStockRequestDone
- 倉庫：warehouse, warehouse/{id}, editWarehouse/{id}, addWarehouse
- 應付帳款：accountPayable, editAccountPayable/{id}, accountPayableCalender
- 應收帳款：accountReceivable, editAccountReceivable/{id}
- 銀行：bank
- 採購單：purchaseOrderDraft, purchaseOrderUnderReview, purchaseOrderApproved, purchaseOrderCompleted, purchaseOrder/{id}, editPurchaseOrder/{id}, addPurchaseOrder
- 報價：projectQuotation, projectQuotation/{id}, EditQuotation/{id}, addProjectQuotation
- BOM：designBOM/{id}, addDesignBOM/{id}, SteelAssemblyBOM/{id}, addSteelAssemblyBOM/{id}, steelBoltBOM/{id}, addSteelBoltBOM/{id}
`.trim();

const MENU_MAP = `
依使用者用的選單名稱選擇正確的 entityType：
- 公司資訊 → entityType=company
- 員工管理 → entityType=employee
- 部門管理 → entityType=department（資料來自目前公司的 departments）
- 專案管理 / 專案列表 / 報價 → entityType=project
- 應收帳款(客戶) → entityType=accountReceivable
- 應付帳款(供應商/信用卡/個人) → entityType=accountPayable
- 銀行 / 銀行管理 → entityType=bank
- 倉庫與儲位管理 → entityType=warehouse
- 商品管理 → entityType=product
- 入庫單管理（已建立/已完成）→ entityType=stockReceipt，可傳 status="done" 或 "created"
- 領料單管理（已建立/已完成）→ entityType=stockRequest，同上
- 採購單（草稿/審核中/已審核/已完成）→ entityType=purchaseOrder，傳 status（draft｜underReview｜approved｜completed）
- 合作方 / 合作方列表 → entityType=partner
- 材料BOM → entityType=materialBom
- 螺絲BOM → entityType=steelBoltBom
- 鋼構件 / 鋼構件清單 → entityType=steelAssembly（需要 buildingId）
- 設計BOM / 設計 BOM / designBOM → entityType=designBom（查整個專案就帶 projectId；設計BOM 即鋼構件清單，底層同一份資料）
- 工項 → entityType=workItem
- 掃描加工紀錄 → entityType=scanHistory
- 案件節點（building/zone/batch/構件包）→ entityType=projectNode
`.trim();

function buildSystemPrompt(currentCompanyId: string, pageContext: PageContext) {
  const isEnglish = i18n.language?.startsWith("en");
  const languageRule = isEnglish
    ? "一律使用英文回覆（系統介面語言為 English，所有回覆文字都用英文，但 entityType、路徑等系統參數保持原樣）"
    : "一律使用繁體中文";
  const isGeneral = isGeneralContext(pageContext);
  const allowedTypes = getAllowedEntities(pageContext);
  const allowedLabels = allowedTypes
    .map((t) => ENTITY_CONFIG[t].label)
    .join("、");
  const permissionSection = isGeneral
    ? "- 此頁面為一般頁面，AI 助理可查詢系統內所有資料類型"
    : `- 此頁面的 AI 助理「只有」以下資料的查詢權限：${allowedLabels}（entityType：${allowedTypes.join("、")}）
- 使用者詢問權限外的資料（其他模組的清單、詳情、數量、金額等）時，**絕對不要**呼叫 listEntities / getEntity / getBankBalance，必須直接告知使用者：這裡是「${pageContext.label}」的 AI 助理，沒有查詢該資料的權限，請前往對應頁面使用該頁面的 AI 助理詢問，並用 navigateTo 引導使用者前往該頁面（用「選單對照」與「系統路徑」判斷正確頁面）
- 即使使用者堅持，也不可查詢權限外的資料；系統會拒絕並回傳「權限不足」`;
  return `你是 SFC ERP 業務管理系統的 AI 助理，負責「${pageContext.label}」頁面，能查詢此頁面權限內的資料、引導使用者前往任何頁面。

# 目前情境
- 目前公司 documentId：${currentCompanyId || "(尚未選擇)"}
- 使用者目前所在頁面：「${pageContext.label}」。本對話是此頁面專屬的對話（每個選單頁面有自己的 AI 助理與記憶）
- 使用者的問題若沒有指明資料類型，優先假設是在問「${pageContext.label}」相關的資料（用選單對照找 entityType）

# 資料權限（最高優先，必須嚴格遵守）
${permissionSection}

# 重要：自動探索流程（不要跟使用者要 documentId！）
使用者通常只會說名稱、編號、特徵，不會知道 documentId。你必須自己查：

1. **先 listEntities 取得清單**（必要時帶名稱關鍵字過濾）
2. **比對使用者敘述**找到對應的 documentId
3. **再 getEntity 取得詳情**

例子：
- 使用者：「公司資訊」→ 若有目前公司 id 就直接 getEntity("company", 目前 id)；若沒有，先 listEntities("company")，若只有一間直接 getEntity，多間就列出讓使用者選
- 使用者：「廣達公司資訊」→ listEntities("company", { name: "廣達" })，從結果找 name 含「廣達」者，再 getEntity 取詳情
- 使用者：「員工 王小明 的資料」→ listEntities("employee", { name: "王小明" })，再 getEntity
- 使用者：「採購單 PO-2024-001」→ listEntities("purchaseOrder", { status: "draft", name: "PO-2024-001" })，找不到再試其他 status
- 使用者：「鋼板 那個商品」→ listEntities("product", { name: "鋼板" })
- 使用者：「已完成的入庫單」→ listEntities("stockReceipt", { status: "done" })
- 使用者：「專案 12225 裡面的設計BOM有幾筆」→ 先 listEntities("project", { name: "12225" }) 找到該案件的 documentId，再 listEntities("designBom", { projectId: 該 documentId })，回傳的筆數就是答案

（以上例子僅在該 entityType 屬於此頁面的資料權限範圍時適用；權限外一律改用 navigateTo 引導）

**絕對不要回應「請提供 documentId」**。你的工作就是用使用者提供的任何資訊自己找出來。
- 同名有多筆 → 列出讓使用者選
- 只有一筆 → 直接用
- 找不到 → 明說「查無符合的 XX」，並建議使用者調整關鍵字或用 navigateTo 到列表頁挑

# 選單對照
${MENU_MAP}

# 可查詢的資料類型 (entityType)（此頁面有權限的範圍）
${allowedTypes.map(
  (t) => `- ${t}（${ENTITY_CONFIG[t].label}）：過濾條件 ${ENTITY_CONFIG[t].filterDescription}`,
).join("\n")}

# 可用工具
${[
  "listEntities(entityType, filters) — 查詢清單（僅限權限內的 entityType）",
  "getEntity(entityType, id) — 取得單筆完整資料（id 必須來自先前 listEntities 的結果；僅限權限內的 entityType）",
  "navigateTo(label, path, reason?) — 產生跳轉連結（任何頁面都可以引導前往）",
  ...(allowedTypes.includes("department") ||
  allowedTypes.includes("employee") ||
  allowedTypes.includes("partner")
    ? [
        `showCreateForm(formType) — 顯示「新增」表單（可直接填寫並送出，不需離開對話）。當使用者要「新增 XX」「如何新增 XX」「新增 XX 要填什麼」時，呼叫此工具，不要只用文字描述欄位：${[
          allowedTypes.includes("department")
            ? "部門→showCreateForm(\"department\")"
            : "",
          allowedTypes.includes("employee")
            ? "員工→showCreateForm(\"employee\")（需填系統已存在的使用者 Email 才能建立）"
            : "",
          allowedTypes.includes("partner")
            ? "合作方→showCreateForm(\"partner\")（公司名稱、統一編號為必填）"
            : "",
        ]
          .filter(Boolean)
          .join("；")}。
   **重要：表單本身已內含「前往該頁面」的按鈕，呼叫 showCreateForm 後絕對不要再呼叫 navigateTo 到同一頁，否則會出現兩個重複連結。**`,
      ]
    : []),
  ...(allowedTypes.includes("employee")
    ? [
        "editEmployee(id) — 顯示「編輯員工」表單以更新員工資料（Email 不可改）。使用者要「修改／更新某員工」時，先用 listEntities(\"employee\", { name }) 找到該員工的 documentId，再呼叫 editEmployee(那個 documentId)。**絕對不要要求使用者提供 documentId。**",
        "setEmployeeStatus(id, action) — 變更員工任職狀態：action=\"resign\"（離職）或 \"unresign\"（復職）。**本系統不提供永久刪除員工**，使用者說「刪除／移除某員工」「某員工離職」時一律用 setEmployeeStatus(documentId, \"resign\")，並向使用者說明這是標記為離職而非永久刪除（日後可復職）。一樣先 listEntities 找到 documentId 再呼叫。",
      ]
    : []),
  ...(allowedTypes.includes("bank")
    ? [
        "getBankBalance(name?) — 查詢銀行餘額。使用者問「銀行餘額多少」「還有多少錢」「XX銀行的餘額」時呼叫此工具，不要用 listEntities(\"bank\")。只有一家銀行時卡片會直接顯示餘額；多家銀行時卡片會顯示下拉選單讓使用者自己選擇，此時你的文字回覆只需請使用者從選單選擇，**絕對不要**在文字中列出各銀行餘額。指定銀行名稱時帶 name 過濾。卡片已內含「前往銀行管理」按鈕，不要再呼叫 navigateTo 到銀行頁。",
      ]
    : []),
]
  .map((t, i) => `${i + 1}. ${t}`)
  .join("\n")}

# 系統路徑
${ROUTE_MAP}

# 回答原則
- ${languageRule}
- 先用「選單對照」判斷 entityType
- 永遠採用「先 list 後 get」的探索流程
- 採購單需要 orderStatus（draft｜underReview｜approved｜completed），若使用者沒指定，預設 draft 先試，必要時再試其他 status
- 不要捏造資料；查詢失敗就明說
- 使用者問「有幾筆／數量／幾個／多少」時，listEntities 回傳開頭的「查到 N 筆」就是答案，直接回覆那個數字；不論筆數多寡都要照實回答
- 清單即使有上百筆也沒問題：表格已自動顯示給使用者，你只需用文字摘要重點（如總筆數）。**絕對不要**回覆「資料過多」「請到列表頁查看」之類拒答的話
- 回答簡潔、條列、可操作
- 已用 showCreateForm 顯示表單時，不要再用 navigateTo 連到同一個新增頁面（表單已自帶按鈕），避免重複連結`;
}

function buildTools(pageContext: PageContext) {
  const allowedTypes = getAllowedEntities(pageContext);
  const allowedForms = FORM_TYPES.filter((f) => allowedTypes.includes(f));
  const tools: any[] = [
  {
    type: "function",
    function: {
      name: "listEntities",
      description:
        "查詢任一資料類型的清單。回傳資料表，欄位依 entityType 不同。",
      parameters: {
        type: "object",
        properties: {
          entityType: { type: "string", enum: allowedTypes },
          name: { type: "string", description: "名稱關鍵字（部分比對）" },
          partner: { type: "string", description: "合作廠商名稱（部分比對）" },
          status: {
            type: "string",
            description:
              "狀態值。purchaseOrder：draft｜underReview｜approved｜completed。stockRequest/stockReceipt：created｜done 等",
          },
          nodeType: {
            type: "string",
            description: "projectNode：building｜zone｜batch｜steelAssemblyPackage",
          },
          projectId: {
            type: "string",
            description:
              "案件 documentId。projectNode 必填；materialBom、steelBoltBom、designBom 查詢時建議帶。workItem 不吃 projectId，要用 projectNodeId",
          },
          projectNodeId: {
            type: "string",
            description: "workItem 必填：工項掛在節點下，沒帶就查不到",
          },
          workItemId: { type: "string" },
          employeeId: { type: "string" },
          steelAssemblyId: { type: "string" },
          buildingId: {
            type: "string",
            description: "steelAssembly 必填：建築節點 documentId",
          },
        },
        required: ["entityType"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "getEntity",
      description: "依 documentId 取得單筆資料完整內容。",
      parameters: {
        type: "object",
        properties: {
          entityType: { type: "string", enum: allowedTypes },
          id: { type: "string" },
          projectId: {
            type: "string",
            description: "案件 documentId。projectNode 必填",
          },
        },
        required: ["entityType", "id"],
      },
    },
  },
  ];
  if (allowedForms.length > 0) {
    tools.push({
    type: "function",
    function: {
      name: "showCreateForm",
      description:
        "當使用者要「新增 XX」「如何新增 XX」「新增 XX 要填寫哪些資訊」時，顯示對應的新增表單（可直接填寫並送出）。department＝新增部門；employee＝新增員工（透過系統已存在的使用者 Email 建立，並填寫基本資料）；partner＝新增合作方（公司名稱、統一編號為必填，含聯絡資訊與營業／發票／送貨地址）。",
      parameters: {
        type: "object",
        properties: {
          formType: { type: "string", enum: allowedForms },
        },
        required: ["formType"],
      },
    },
    });
  }
  if (allowedTypes.includes("employee")) {
    tools.push({
      type: "function",
      function: {
        name: "editEmployee",
        description:
          "顯示「編輯員工」表單以更新員工資料（姓名、性別、生日、駕照、地址、緊急聯絡人、備註等；Email 不可改）。id 必須是先前 listEntities(\"employee\") 取得的員工 documentId。使用者要「修改／更新某員工」時，先用 listEntities 找到該員工的 documentId，再呼叫此工具，不要要求使用者提供 documentId。",
        parameters: {
          type: "object",
          properties: {
            id: { type: "string", description: "員工 documentId" },
          },
          required: ["id"],
        },
      },
    });
    tools.push({
      type: "function",
      function: {
        name: "setEmployeeStatus",
        description:
          "變更員工任職狀態並顯示確認卡片。action=\"resign\"（離職）或\"unresign\"（復職）。本系統不支援永久刪除員工，使用者說「刪除員工／移除員工／某員工離職」時一律用 action=\"resign\"。id 必須是先前 listEntities(\"employee\") 取得的員工 documentId。先用 listEntities 找到 documentId 再呼叫，不要要求使用者提供。",
        parameters: {
          type: "object",
          properties: {
            id: { type: "string", description: "員工 documentId" },
            action: { type: "string", enum: ["resign", "unresign"] },
          },
          required: ["id", "action"],
        },
      },
    });
  }
  if (allowedTypes.includes("bank")) {
    tools.push({
    type: "function",
    function: {
      name: "getBankBalance",
      description:
        "查詢銀行餘額。當使用者詢問「銀行餘額多少」「XX銀行還有多少錢」時呼叫。只有一家銀行時直接顯示餘額；多家銀行時會顯示下拉選單讓使用者選擇銀行後查看餘額。可帶 name 過濾特定銀行。",
      parameters: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description: "銀行名稱關鍵字（部分比對），不帶則查全部銀行",
          },
        },
      },
    },
    });
  }
  tools.push({
    type: "function",
    function: {
      name: "navigateTo",
      description: "產生前端頁面連結按鈕引導使用者跳轉。",
      parameters: {
        type: "object",
        properties: {
          label: { type: "string" },
          path: { type: "string" },
          reason: { type: "string" },
        },
        required: ["label", "path"],
      },
    },
  });
  return tools;
}

function formatValue(val: any, format?: ColumnDef["format"]): any {
  if (val == null || val === "") return "—";
  if (format === "address" && typeof val === "object" && !Array.isArray(val)) {
    const { postalCode, city, district, address } = val;
    const parts = [postalCode, city, district, address].filter(
      (p) => p != null && p !== "",
    );
    return parts.length ? parts.join(" ") : "—";
  }
  if (format === "contacts" && Array.isArray(val)) {
    if (!val.length) return "—";
    return val
      .map((c) => {
        const parts = [c?.name, c?.department, c?.telephone, c?.email].filter(
          (p) => p != null && p !== "",
        );
        return parts.join(" / ");
      })
      .filter(Boolean)
      .join("； ") || "—";
  }
  if (Array.isArray(val))
    return val.filter(Boolean).join("、") || "—";
  if (format === "date") return dayjs(val).format("YYYY-MM-DD");
  if (format === "datetime") return dayjs(val).format("YYYY-MM-DD HH:mm");
  if (format === "money") return Number(val).toLocaleString();
  if (typeof val === "object") return JSON.stringify(val);
  return val;
}

async function executeTool(
  name: string,
  args: any,
  pageContext: PageContext,
): Promise<{ content: string; ui?: UIPayload }> {
  const allowedTypes = getAllowedEntities(pageContext);
  const denyContent = (label: string) =>
    `權限不足：「${pageContext.label}」頁面的 AI 助理沒有「${label}」的查詢權限，僅能查詢：${allowedTypes
      .map((t) => ENTITY_CONFIG[t].label)
      .join(
        "、",
      )}。請告知使用者前往對應頁面，使用該頁面的 AI 助理詢問，並用 navigateTo 引導前往。`;
  if (name === "navigateTo") {
    return {
      content: `已產生連結：${args.label} → ${args.path}`,
      ui: {
        kind: "navigate",
        label: String(args.label ?? "前往"),
        path: String(args.path ?? "/"),
        reason: args.reason,
      },
    };
  }
  if (name === "showCreateForm") {
    const form = FORM_CONFIG[args.formType as FormType];
    if (!form) return { content: `尚不支援的表單：${args.formType}` };
    if (!allowedTypes.includes(args.formType as EntityType)) {
      return { content: denyContent(form.title) };
    }
    const fieldLines = form.fields
      .filter((f) => f.type !== "section")
      .map(
        (f) =>
          `- ${f.label}${f.required ? "（必填）" : ""}${f.hint ? `：${f.hint}` : ""}`,
      )
      .join("\n");
    const requiredLabels = form.fields
      .filter((f) => f.required)
      .map((f) => f.label)
      .join("、");
    const action =
      form.mode === "info"
        ? "已在下方依後端資料定義列出需填寫的欄位（標 * 為必填），使用者可點按鈕前往完整頁面新增。"
        : "使用者可直接在下方表單填寫並送出。";
    return {
      content:
        `已顯示「${form.title}」表單。${action}\n需填寫的欄位：\n${fieldLines}\n` +
        `必填欄位：${requiredLabels || "（無）"}。請簡短向使用者說明。`,
      ui: { kind: "createForm", formType: args.formType },
    };
  }
  if (name === "editEmployee") {
    if (!allowedTypes.includes("employee")) {
      return { content: denyContent("員工") };
    }
    if (!args.id) return { content: "請先用 listEntities 找到員工的 documentId。" };
    try {
      const res = await single(orpcClient.employee.read([{ id: args.id }]));
      const record = flattenDetail(res);
      if (!record.id) {
        return { content: `查無 documentId 為 ${args.id} 的員工。` };
      }
      return {
        content:
          `已顯示員工「${record.name ?? args.id}」的編輯表單，使用者可直接修改欄位並送出（Email 不可修改）。請簡短說明。`,
        ui: { kind: "employeeEdit", record },
      };
    } catch (e: any) {
      return { content: `讀取員工資料失敗：${e?.message ?? String(e)}` };
    }
  }
  if (name === "setEmployeeStatus") {
    if (!allowedTypes.includes("employee")) {
      return { content: denyContent("員工") };
    }
    if (!args.id) return { content: "請先用 listEntities 找到員工的 documentId。" };
    const action = args.action === "unresign" ? "unresign" : "resign";
    try {
      const res = await single(orpcClient.employee.read([{ id: args.id }]));
      const record = flattenDetail(res);
      if (!record.id) {
        return { content: `查無 documentId 為 ${args.id} 的員工。` };
      }
      const actionLabel = action === "resign" ? "離職" : "復職";
      return {
        content:
          `本系統不提供「刪除員工」，僅能將員工標記為離職（resigned）或復職（normal）。` +
          `已顯示「${record.name ?? args.id}」的${actionLabel}確認卡片，請使用者點按鈕確認。目前狀態：${record.status ?? "未知"}。`,
        ui: { kind: "employeeStatus", record, action },
      };
    } catch (e: any) {
      return { content: `讀取員工資料失敗：${e?.message ?? String(e)}` };
    }
  }
  if (name === "getBankBalance") {
    if (!allowedTypes.includes("bank")) {
      return { content: denyContent("銀行餘額") };
    }
    try {
      const res = await orpcClient.bank.list({});
      let banks = flattenList(res).map((b) => ({
        id: b.id,
        name: String(b.name ?? "(未命名)"),
        balance: Number(b.balance ?? 0),
        remarks: b.remarks ?? undefined,
      }));
      if (args.name) {
        const q = String(args.name).toLowerCase();
        banks = banks.filter((b) => b.name.toLowerCase().includes(q));
      }
      if (banks.length === 0) {
        return {
          content: args.name
            ? `查無名稱含「${args.name}」的銀行。`
            : "目前沒有任何銀行資料。",
        };
      }
      if (banks.length === 1) {
        return {
          content: `${banks[0].name} 的餘額為 ${banks[0].balance.toLocaleString()}，已顯示餘額卡片給使用者。`,
          ui: { kind: "bankBalance", banks },
        };
      }
      return {
        content:
          `查到 ${banks.length} 家銀行：${banks.map((b) => b.name).join("、")}。` +
          `已顯示下拉選單，請簡短告知使用者從選單中選擇銀行即可查看餘額；不要在文字中列出各銀行的餘額。`,
        ui: { kind: "bankBalance", banks },
      };
    } catch (e: any) {
      return { content: `查詢失敗：${e?.message ?? String(e)}` };
    }
  }
  if (name === "listEntities") {
    const cfg = ENTITY_CONFIG[args.entityType as EntityType];
    if (!cfg) return { content: `不支援的 entityType：${args.entityType}` };
    if (!allowedTypes.includes(args.entityType as EntityType)) {
      return { content: denyContent(cfg.label) };
    }
    try {
      const res = await cfg.listFn(args);
      let rows = flattenList(res);
      rows = filterRows(rows, args);
      if (rows.length === 0) {
        const rawKeys =
          res && typeof res === "object" ? Object.keys(res).join(",") : "(空)";
        return {
          content: `listEntities("${args.entityType}") 解析後沒有 row。原始回應頂層 keys: [${rawKeys}]。原始回應片段：${JSON.stringify(res).slice(0, 500)}`,
        };
      }
      const sampleRow = rows[0];
      const summary =
        `查到 ${rows.length} 筆 ${cfg.label}。\n` +
        `每筆可用的欄位 keys：${Object.keys(sampleRow).slice(0, 30).join(",")}\n` +
        rows
          .slice(0, 10)
          .map((r, i) => {
            const first = cfg.columns[0];
            const v = getNested(r, first.key);
            return `${i + 1}. ${v ?? "(無)"}（id: ${r.id}）`;
          })
          .join("\n");
      return {
        content: summary,
        ui: {
          kind: "entityList",
          entityType: args.entityType,
          rows,
          columns: cfg.columns,
          itemPath: cfg.itemPath,
        },
      };
    } catch (e: any) {
      return { content: `查詢失敗：${e?.message ?? String(e)}` };
    }
  }
  if (name === "getEntity") {
    const cfg = ENTITY_CONFIG[args.entityType as EntityType];
    if (!cfg) return { content: `不支援的 entityType：${args.entityType}` };
    if (!allowedTypes.includes(args.entityType as EntityType)) {
      return { content: denyContent(cfg.label) };
    }
    if (!args.id) return { content: "請提供 documentId。" };
    try {
      const res = await cfg.getFn(args.id, args);
      const record = flattenDetail(res);
      const lines = cfg.detailFields
        .map((f) => {
          const v = formatValue(getNested(record, f.key), f.format);
          return `- ${f.label}：${v}`;
        })
        .join("\n");
      return {
        content:
          `${cfg.label} 詳情：\n${lines}\n\n` +
          `完整原始資料（JSON，回答任何欄位都必須以此為準，沒有的值就說「無」，不要臆測）：\n` +
          JSON.stringify(record).slice(0, 3000),
        ui: {
          kind: "entityDetail",
          entityType: args.entityType,
          record,
          itemPath: cfg.itemPath?.(record.id),
        },
      };
    } catch (e: any) {
      return { content: `查詢失敗：${e?.message ?? String(e)}` };
    }
  }
  return { content: `Unknown tool: ${name}` };
}

async function streamOpenRouter(
  messages: any[],
  tools: any[],
  signal: AbortSignal,
  onContent: (full: string) => void,
): Promise<{ content: string; toolCalls: any[] }> {
  const apiKey = import.meta.env.VITE_OPENROUTER_API_KEY;
  const res = await fetch(OPENROUTER_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
      "HTTP-Referer": window.location.origin,
      "X-Title": "SFC testing",
    },
    body: JSON.stringify({
      model: import.meta.env.VITE_OPENROUTER_MODEL || "google/gemini-2.5-flash",
      messages,
      tools,
      tool_choice: "auto",
      stream: true,
    }),
    signal,
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${text}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let content = "";
  const toolCalls: any[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      const data = trimmed.slice(5).trim();
      if (data === "[DONE]") continue;
      let json: any;
      try {
        json = JSON.parse(data);
      } catch {
        continue;
      }
      const delta = json.choices?.[0]?.delta;
      if (!delta) continue;
      if (delta.content) {
        content += delta.content;
        onContent(content);
      }
      if (delta.tool_calls) {
        for (const tc of delta.tool_calls) {
          const idx = tc.index ?? 0;
          if (!toolCalls[idx]) {
            toolCalls[idx] = {
              id: tc.id,
              type: "function",
              function: { name: "", arguments: "" },
            };
          }
          if (tc.id) toolCalls[idx].id = tc.id;
          if (tc.function?.name) toolCalls[idx].function.name += tc.function.name;
          if (tc.function?.arguments)
            toolCalls[idx].function.arguments += tc.function.arguments;
        }
      }
    }
  }

  return { content, toolCalls: toolCalls.filter(Boolean) };
}

type DrawingPdfHandlers = {
  lookup: (text: string) => DrawingPdf | null;
  openPdf: (file: DrawingPdf) => void;
};

const DrawingPdfContext = createContext<DrawingPdfHandlers | null>(null);

export function hastNodeText(node: any): string {
  if (!node) return "";
  if (typeof node.value === "string") return node.value;
  if (!Array.isArray(node.children)) return "";
  return node.children.map((child: any) => hastNodeText(child)).join("");
}

function Markdown({ children }: { children: string }) {
  const drawings = useContext(DrawingPdfContext);
  return (
    <div className="min-w-0 text-sm leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_code]:rounded [&_code]:bg-black/5 [&_code]:px-1 [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_pre_code]:bg-transparent [&_pre_code]:px-0 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_*:first-child]:mt-0 [&_*:last-child]:mb-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          table: ({ node, ...props }) => (
            <div className="my-1 max-w-full overflow-x-auto overscroll-x-contain rounded border border-gray-200">
              <table {...props} className="w-max min-w-full text-xs" />
            </div>
          ),
          th: ({ node, ...props }) => (
            <th
              {...props}
              className="whitespace-nowrap border-b border-r border-gray-200 bg-gray-50 px-2 py-1 text-left font-medium last:border-r-0"
            />
          ),
          td: ({ node, children, ...props }) => {
            const pdf = drawings ? drawings.lookup(hastNodeText(node)) : null;
            return (
              <td
                {...props}
                className="whitespace-nowrap border-b border-r border-gray-200 px-2 py-1 align-top last:border-r-0"
              >
                {pdf ? (
                  <button
                    type="button"
                    title={pdf.name}
                    onClick={() => drawings?.openPdf(pdf)}
                    className="cursor-pointer text-blue-600 underline hover:text-blue-700"
                  >
                    {children}
                  </button>
                ) : (
                  children
                )}
              </td>
            );
          },
          pre: ({ node, ...props }) => (
            <pre
              {...props}
              className="my-1 overflow-x-auto rounded bg-black/5 p-2 text-xs"
            />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

function NavigateCard({
  payload,
  onGo,
}: {
  payload: Extract<UIPayload, { kind: "navigate" }>;
  onGo: (path: string) => void;
}) {
  return (
    <div className="mt-2 rounded-xl border border-blue-200 bg-blue-50 p-3">
      {payload.reason && (
        <div className="mb-2 text-xs text-gray-600">{payload.reason}</div>
      )}
      <Button type="primary" onClick={() => onGo(payload.path)}>
        {payload.label}
      </Button>
      <div className="mt-1 text-[10px] text-gray-400">{payload.path}</div>
    </div>
  );
}

function EntityListCard({
  payload,
  onGo,
}: {
  payload: Extract<UIPayload, { kind: "entityList" }>;
  onGo: (path: string) => void;
}) {
  if (!payload.rows.length) return null;
  const itemPath = payload.itemPath ?? ENTITY_CONFIG[payload.entityType]?.itemPath;
  const cols = payload.columns.map((c) => ({
    title: c.title,
    dataIndex: c.key,
    key: c.key,
    render: (_: any, row: Record<string, any>) => {
      const v = formatValue(getNested(row, c.key), c.format);
      if (c === payload.columns[0] && itemPath) {
        return <a onClick={() => onGo(itemPath(row.id))}>{v}</a>;
      }
      if (c.format === "tag") return v !== "—" ? <Tag>{v}</Tag> : "—";
      return v;
    },
  }));
  return (
    <div className="mt-2 rounded-xl border border-gray-200 bg-white p-2">
      <Table
        size="small"
        rowKey="id"
        dataSource={payload.rows}
        pagination={false}
        scroll={{ x: true, y: 220 }}
        columns={cols}
      />
    </div>
  );
}

function EntityDetailCard({
  payload,
  onGo,
}: {
  payload: Extract<UIPayload, { kind: "entityDetail" }>;
  onGo: (path: string) => void;
}) {
  const cfg = ENTITY_CONFIG[payload.entityType];
  return (
    <div className="mt-2 space-y-1 rounded-xl border border-gray-200 bg-white p-3 text-sm">
      <div className="text-base font-semibold">
        {cfg.label}
        {(payload.record.name ||
          payload.record.code ||
          payload.record.orderNumber) && (
          <span className="ml-2 text-gray-700">
            {payload.record.name ??
              payload.record.code ??
              payload.record.orderNumber}
          </span>
        )}
      </div>
      {cfg.detailFields.map((f) => {
        const v = formatValue(getNested(payload.record, f.key), f.format);
        return (
          <div key={f.key} className="text-gray-700">
            <span className="text-gray-500">{f.label}：</span>
            <span>{v}</span>
          </div>
        );
      })}
      {payload.itemPath && (
        <Button
          type="primary"
          size="small"
          className="mt-2"
          onClick={() => onGo(payload.itemPath!)}
        >
          開啟頁面
        </Button>
      )}
    </div>
  );
}

function CreateFormCard({
  payload,
  onGo,
}: {
  payload: Extract<UIPayload, { kind: "createForm" }>;
  onGo: (path: string) => void;
}) {
  const form = FORM_CONFIG[payload.formType];
  const isInfo = form.mode === "info";
  const [values, setValues] = useState<Record<string, any>>({});
  const [empOptions, setEmpOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const needsEmployees =
    !isInfo && form.fields.some((f) => f.type === "employeeMulti");

  useEffect(() => {
    if (!needsEmployees) return;
    let alive = true;
    fetchCompanyEmployeeOptions()
      .then((opts) => {
        if (alive) setEmpOptions(opts);
      })
      .catch(() => {
        if (alive) setEmpOptions([]);
      });
    return () => {
      alive = false;
    };
  }, [needsEmployees]);

  const setField = (key: string, val: any) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    const missing = form.fields.find(
      (f) => f.required && !String(values[f.key] ?? "").trim(),
    );
    if (missing) {
      message.warning(`請填寫「${missing.label}」`);
      return;
    }
    if (!form.submit) return;
    setSubmitting(true);
    try {
      const res: any = await form.submit(values);
      const ok = form.isSuccess ? form.isSuccess(res) : !!res?.data;
      if (ok) {
        setDone(true);
        message.success(`${form.title}成功`);
      } else {
        message.error(`${form.title}失敗，請稍後再試或前往完整頁面`);
      }
    } catch (e: any) {
      message.error(`${form.title}失敗：${e?.message ?? String(e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mt-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm">
        <div className="mb-2 font-semibold text-green-700">
          ✅ {form.title}成功
        </div>
        <Button
          type="primary"
          size="small"
          onClick={() => onGo(form.listPath ?? form.addPath)}
        >
          {form.listLabel ?? "查看列表"}
        </Button>
      </div>
    );
  }

  if (isInfo) {
    return (
      <div className="mt-2 space-y-3 rounded-xl border border-gray-200 bg-white p-3 text-sm">
        <div>
          <div className="text-base font-semibold">{form.title}</div>
          <div className="text-xs text-gray-500">{form.description}</div>
        </div>
        <div className="space-y-1.5">
          {form.fields.map((f) => (
            <div
              key={f.key}
              className="flex items-baseline justify-between gap-2 border-b border-gray-100 pb-1.5 last:border-0"
            >
              <div className="text-gray-700">
                {f.label}
                {f.required && <span className="ml-0.5 text-red-500">*</span>}
              </div>
              {f.hint && (
                <div className="text-right text-xs text-gray-400">{f.hint}</div>
              )}
            </div>
          ))}
        </div>
        <Button
          type="primary"
          size="small"
          onClick={() => onGo(form.addPath)}
        >
          前往新增{form.title.replace("新增", "")}頁面
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-3 rounded-xl border border-gray-200 bg-white p-3 text-sm">
      <div>
        <div className="text-base font-semibold">{form.title}</div>
        <div className="text-xs text-gray-500">{form.description}</div>
      </div>
      {form.fields.map((f) =>
        f.type === "section" ? (
          <div
            key={f.key}
            className="border-b border-gray-200 pb-1 pt-2 text-sm font-semibold text-gray-700 first:pt-0"
          >
            {f.label}
          </div>
        ) : (
        <div key={f.key} className="space-y-1">
          <div className="text-gray-600">
            {f.label}
            {f.required && <span className="ml-0.5 text-red-500">*</span>}
            {f.hint && (
              <span className="ml-1 text-xs text-gray-400">（{f.hint}）</span>
            )}
          </div>
          {f.type === "text" && (
            <Input
              value={values[f.key] ?? ""}
              placeholder={f.placeholder}
              onChange={(e) => setField(f.key, e.target.value)}
            />
          )}
          {f.type === "textarea" && (
            <Input.TextArea
              value={values[f.key] ?? ""}
              placeholder={f.placeholder}
              autoSize={{ minRows: 2, maxRows: 4 }}
              onChange={(e) => setField(f.key, e.target.value)}
            />
          )}
          {f.type === "employeeMulti" && (
            <Select
              mode="multiple"
              allowClear
              className="w-full"
              placeholder="輸入或選擇員工"
              optionFilterProp="label"
              value={values[f.key] ?? []}
              options={empOptions}
              onChange={(val) => setField(f.key, val)}
            />
          )}
          {f.type === "select" && (
            <Select
              allowClear
              className="w-full"
              placeholder={f.placeholder ?? "請選擇"}
              value={values[f.key] ?? undefined}
              options={f.options ?? []}
              onChange={(val) => setField(f.key, val)}
            />
          )}
          {f.type === "date" && (
            <DatePicker
              className="w-full"
              value={values[f.key] ? dayjs(values[f.key]) : undefined}
              onChange={(d) =>
                setField(f.key, d ? d.format("YYYY-MM-DD") : undefined)
              }
            />
          )}
        </div>
        ),
      )}
      <div className="flex gap-2 pt-1">
        <Button
          type="primary"
          size="small"
          loading={submitting}
          onClick={handleSubmit}
        >
          送出
        </Button>
        <Button size="small" onClick={() => onGo(form.addPath)}>
          前往完整頁面
        </Button>
      </div>
    </div>
  );
}

function BankBalanceCard({
  payload,
  onGo,
}: {
  payload: Extract<UIPayload, { kind: "bankBalance" }>;
  onGo: (path: string) => void;
}) {
  const single = payload.banks.length === 1;
  const [selectedId, setSelectedId] = useState<string | undefined>(
    single ? payload.banks[0].id : undefined,
  );
  const selected = payload.banks.find((b) => b.id === selectedId);
  return (
    <div className="mt-2 space-y-2 rounded-xl border border-gray-200 bg-white p-3 text-sm">
      <div className="text-base font-semibold">銀行餘額</div>
      {!single && (
        <Select
          className="w-full"
          placeholder="請選擇銀行"
          value={selectedId}
          options={payload.banks.map((b) => ({ label: b.name, value: b.id }))}
          onChange={(val) => setSelectedId(val)}
        />
      )}
      {selected ? (
        <div className="rounded-lg bg-blue-50 p-3">
          <div className="text-gray-600">{selected.name}</div>
          <div className="text-xl font-bold text-blue-600">
            {Number(selected.balance).toLocaleString()}
          </div>
          {selected.remarks && (
            <div className="mt-1 text-xs text-gray-400">{selected.remarks}</div>
          )}
        </div>
      ) : (
        <div className="text-xs text-gray-400">
          請從上方選單選擇銀行以查看餘額
        </div>
      )}
      <Button size="small" onClick={() => onGo("/hrMainPage/bank")}>
        前往銀行管理
      </Button>
    </div>
  );
}

const EMPLOYEE_EDIT_FIELDS: FormFieldDef[] = [
  { key: "name", label: "姓名", type: "text", required: true },
  {
    key: "gender",
    label: "生理性別",
    type: "select",
    options: [
      { label: "男生", value: "male" },
      { label: "女生", value: "female" },
    ],
  },
  { key: "birthday", label: "生日", type: "date" },
  { key: "bloodType", label: "血型", type: "text" },
  { key: "nationalIdNo", label: "身分證字號", type: "text" },
  {
    key: "motorbikeLicense",
    label: "機車駕照",
    type: "select",
    options: [
      { label: "有", value: "true" },
      { label: "沒有", value: "false" },
    ],
  },
  {
    key: "carLicense",
    label: "汽車駕照",
    type: "select",
    options: [
      { label: "有", value: "true" },
      { label: "沒有", value: "false" },
    ],
  },
  { key: "city", label: "縣市", type: "text" },
  { key: "district", label: "行政區", type: "text" },
  { key: "postalCode", label: "郵遞區號", type: "text" },
  { key: "addressLine", label: "地址", type: "text" },
  { key: "emergencyContact", label: "緊急聯絡人", type: "text" },
  {
    key: "emergencyContactRelationship",
    label: "緊急聯絡人關係",
    type: "text",
  },
  {
    key: "emergencyContactPhoneNumber",
    label: "緊急聯絡人電話",
    type: "text",
  },
  { key: "note", label: "備註", type: "textarea" },
];

function EmployeeEditCard({
  payload,
  onGo,
}: {
  payload: Extract<UIPayload, { kind: "employeeEdit" }>;
  onGo: (path: string) => void;
}) {
  const r = payload.record;
  const email = getNested(r, "user.email") ?? "";
  const [values, setValues] = useState<Record<string, any>>(() => ({
    name: r.name ?? "",
    gender: r.gender || undefined,
    birthday: r.birthday ? dayjs(r.birthday).format("YYYY-MM-DD") : undefined,
    bloodType: r.bloodType ?? "",
    nationalIdNo: r.nationalIdNo ?? "",
    motorbikeLicense: r.motorbikeLicense ? "true" : "false",
    carLicense: r.carLicense ? "true" : "false",
    city: r.address?.city ?? "",
    district: r.address?.district ?? "",
    postalCode:
      r.address?.postalCode != null ? String(r.address.postalCode) : "",
    addressLine: r.address?.address ?? "",
    emergencyContact: r.emergencyContact ?? "",
    emergencyContactRelationship: r.emergencyContactRelationship ?? "",
    emergencyContactPhoneNumber: r.emergencyContactPhoneNumber ?? "",
    note: r.note ?? "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const setField = (key: string, val: any) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    if (!String(values.name ?? "").trim()) {
      message.warning("請填寫「姓名」");
      return;
    }
    setSubmitting(true);
    try {
      const hasAddress =
        values.city &&
        values.district &&
        values.postalCode &&
        values.addressLine;
      const updateData: any = {
        id: r.id,
        name: values.name ?? "",
        gender: values.gender || undefined,
        bloodType: values.bloodType || undefined,
        nationalIdNo: values.nationalIdNo || undefined,
        motorbikeLicense: values.motorbikeLicense === "true",
        carLicense: values.carLicense === "true",
        address: hasAddress
          ? {
              city: values.city,
              district: values.district,
              postalCode: Number(values.postalCode),
              address: values.addressLine,
            }
          : null,
        emergencyContact: values.emergencyContact || undefined,
        emergencyContactRelationship:
          values.emergencyContactRelationship || undefined,
        emergencyContactPhoneNumber:
          values.emergencyContactPhoneNumber || undefined,
        note: values.note || undefined,
      };
      if (values.birthday) updateData.birthday = new Date(values.birthday);
      const res: any = await single(orpcClient.employee.update([updateData]));
      if (res?.id) {
        setDone(true);
        message.success("員工資料已更新");
      } else {
        message.error("更新失敗，請稍後再試");
      }
    } catch (e: any) {
      message.error(`更新失敗：${e?.message ?? String(e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mt-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm">
        <div className="mb-2 font-semibold text-green-700">✅ 員工資料已更新</div>
        <Button
          type="primary"
          size="small"
          onClick={() => onGo(`/hrMainPage/employee/${r.id}`)}
        >
          開啟員工頁面
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-3 rounded-xl border border-gray-200 bg-white p-3 text-sm">
      <div>
        <div className="text-base font-semibold">
          編輯員工{r.name ? `：${r.name}` : ""}
        </div>
        <div className="text-xs text-gray-500">修改欄位後送出即可更新</div>
      </div>
      <div className="space-y-1">
        <div className="text-gray-600">Email（不可修改）</div>
        <Input value={email} disabled />
      </div>
      {EMPLOYEE_EDIT_FIELDS.map((f) => (
        <div key={f.key} className="space-y-1">
          <div className="text-gray-600">
            {f.label}
            {f.required && <span className="ml-0.5 text-red-500">*</span>}
          </div>
          {f.type === "text" && (
            <Input
              value={values[f.key] ?? ""}
              onChange={(e) => setField(f.key, e.target.value)}
            />
          )}
          {f.type === "textarea" && (
            <Input.TextArea
              value={values[f.key] ?? ""}
              autoSize={{ minRows: 2, maxRows: 4 }}
              onChange={(e) => setField(f.key, e.target.value)}
            />
          )}
          {f.type === "select" && (
            <Select
              allowClear
              className="w-full"
              placeholder="請選擇"
              value={values[f.key] ?? undefined}
              options={f.options ?? []}
              onChange={(val) => setField(f.key, val)}
            />
          )}
          {f.type === "date" && (
            <DatePicker
              className="w-full"
              value={values[f.key] ? dayjs(values[f.key]) : undefined}
              onChange={(d) =>
                setField(f.key, d ? d.format("YYYY-MM-DD") : undefined)
              }
            />
          )}
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <Button
          type="primary"
          size="small"
          loading={submitting}
          onClick={handleSubmit}
        >
          儲存
        </Button>
        <Button size="small" onClick={() => onGo(`/hrMainPage/editEmployee/${r.id}`)}>
          前往完整頁面
        </Button>
      </div>
    </div>
  );
}

function EmployeeStatusCard({
  payload,
  onGo,
}: {
  payload: Extract<UIPayload, { kind: "employeeStatus" }>;
  onGo: (path: string) => void;
}) {
  const r = payload.record;
  const isResign = payload.action === "resign";
  const actionLabel = isResign ? "離職" : "復職";
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      if (isResign) {
        await orpcClient.employee.resign({ employeeId: r.id });
      } else {
        await orpcClient.employee.unresign({ employeeId: r.id });
      }
      setDone(true);
      message.success(`已將「${r.name ?? r.id}」標記為${actionLabel}`);
    } catch (e: any) {
      message.error(`${actionLabel}失敗：${e?.message ?? String(e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mt-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm">
        <div className="mb-2 font-semibold text-green-700">
          ✅ 已將「{r.name ?? r.id}」標記為{actionLabel}
        </div>
        <Button
          type="primary"
          size="small"
          onClick={() => onGo("/hrMainPage/employees")}
        >
          查看員工列表
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`mt-2 space-y-2 rounded-xl border p-3 text-sm ${
        isResign ? "border-red-200 bg-red-50" : "border-blue-200 bg-blue-50"
      }`}
    >
      <div className="text-base font-semibold">員工{actionLabel}</div>
      <div className="text-gray-700">
        確定要將「{r.name ?? r.id}」標記為{actionLabel}嗎？
      </div>
      <div className="text-xs text-gray-500">
        {isResign
          ? "本系統不支援永久刪除員工；離職後員工狀態會變為 resigned，日後仍可復職。"
          : "員工狀態將恢復為正常（normal）。"}
      </div>
      <div className="text-xs text-gray-400">目前狀態：{r.status ?? "未知"}</div>
      <div className="flex gap-2 pt-1">
        <Button
          type="primary"
          danger={isResign}
          size="small"
          loading={submitting}
          onClick={handleConfirm}
        >
          確認{actionLabel}
        </Button>
        <Button
          size="small"
          onClick={() => onGo(`/hrMainPage/employee/${r.id}`)}
        >
          開啟員工頁面
        </Button>
      </div>
    </div>
  );
}

function navigateDuplicatesForm(
  messages: ChatMessage[],
  idx: number,
  path: string,
): boolean {
  let start = idx;
  while (start > 0 && messages[start - 1].role === "tool") start--;
  let end = idx;
  while (end < messages.length - 1 && messages[end + 1].role === "tool") end++;
  for (let j = start; j <= end; j++) {
    const ui = messages[j].ui;
    if (ui?.kind === "createForm" && FORM_CONFIG[ui.formType]?.addPath === path) {
      return true;
    }
  }
  return false;
}

export default function ChatBot({
  docked,
  open: openProp,
  onClose: onCloseProp,
  chatContext,
  onDataChanged,
}: {
  docked?: boolean;
  open?: boolean;
  onClose?: () => void;
  chatContext?: ChatContextInfo;
  onDataChanged?: () => void;
} = {}) {
  const { pathname } = useLocation();
  const pageContext = getPageContext(pathname);
  const useBackend = BACKEND_PAGE_KEYS.has(pageContext.key);
  const controlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlled ? !!openProp : uncontrolledOpen;
  const setOpen = setUncontrolledOpen;
  const closeChat = () => {
    if (controlled) onCloseProp?.();
    else setUncontrolledOpen(false);
  };
  const [chatWidth, setChatWidth] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(WIDTH_KEY));
      return Number.isFinite(saved) && saved > 0
        ? clampChatWidth(saved)
        : CHAT_WIDTH;
    } catch {
      return CHAT_WIDTH;
    }
  });
  const [resizing, setResizing] = useState(false);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = chatWidth;
    let latest = startWidth;
    setResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    const onMove = (ev: MouseEvent) => {
      latest = clampChatWidth(startWidth + (startX - ev.clientX));
      setChatWidth(latest);
    };
    const onUp = () => {
      setResizing(false);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      try {
        localStorage.setItem(WIDTH_KEY, String(latest));
      } catch {
        // ignore
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const resetWidth = () => {
    setChatWidth(CHAT_WIDTH);
    try {
      localStorage.setItem(WIDTH_KEY, String(CHAT_WIDTH));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!docked) return;
    const body = document.body;
    if (openProp) {
      body.classList.add("chat-docked-open");
      body.style.setProperty("--chat-docked-width", `${chatWidth}px`);
    } else {
      body.classList.remove("chat-docked-open");
      body.style.removeProperty("--chat-docked-width");
    }
    return () => {
      body.classList.remove("chat-docked-open");
      body.style.removeProperty("--chat-docked-width");
    };
  }, [docked, openProp, chatWidth]);

  useEffect(() => {
    if (!docked) return;
    document.body.classList.toggle("chat-resizing", resizing);
    return () => document.body.classList.remove("chat-resizing");
  }, [docked, resizing]);

  useEffect(() => {
    if (!docked) return;
    const onWindowResize = () => setChatWidth((w) => clampChatWidth(w));
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [docked]);

  if (docked) {
    return (
      <aside
        className="relative z-[1010] h-screen shrink-0 overflow-hidden border-l border-gray-200 bg-white"
        style={{
          width: openProp ? chatWidth : 0,
          transition: resizing ? "none" : "width 300ms ease-in-out",
        }}
      >
        {openProp && (
          <div
            onMouseDown={startResize}
            onDoubleClick={resetWidth}
            title="拖曳調整寬度（雙擊還原預設）"
            className={`group absolute left-0 top-0 z-20 flex h-full w-2.5 cursor-col-resize touch-none select-none items-center justify-center transition-colors ${
              resizing ? "bg-blue-400/60" : "bg-transparent hover:bg-blue-200/60"
            }`}
          >
            <div
              className={`h-10 w-1 rounded-full transition-colors ${
                resizing ? "bg-blue-600" : "bg-gray-300 group-hover:bg-blue-500"
              }`}
            />
          </div>
        )}
        <div className="h-full" style={{ width: chatWidth }}>
          {useBackend ? (
            <BackendChatPanel
              key={getBackendChatKey(pageContext.key)}
              pageContext={pageContext}
              open={!!openProp}
              onClose={onCloseProp ?? (() => {})}
              chatContext={chatContext}
              onDataChanged={onDataChanged}
              docked
            />
          ) : (
            <ChatPanel
              key={pageContext.key}
              pageContext={pageContext}
              open={!!openProp}
              onClose={onCloseProp ?? (() => {})}
              docked
            />
          )}
        </div>
      </aside>
    );
  }

  if (pathname.startsWith("/hrMainPage")) return null;

  return (
    <>
      {!controlled && !open && (
        <FloatButton
          icon={
            <img
              src={aiAgentIcon}
              alt="AI 助理"
              style={{ width: 32, height: 32, objectFit: "contain" }}
            />
          }
          tooltip={`AI 助理（${pageContext.label}）`}
          onClick={() => setOpen(true)}
          style={{ right: 24, bottom: 24, width: 56, height: 56 }}
        />
      )}
      {useBackend ? (
        <BackendChatPanel
          key={getBackendChatKey(pageContext.key)}
          pageContext={pageContext}
          open={open}
          onClose={closeChat}
          chatContext={chatContext}
          onDataChanged={onDataChanged}
        />
      ) : (
        <ChatPanel
          key={pageContext.key}
          pageContext={pageContext}
          open={open}
          onClose={closeChat}
        />
      )}
    </>
  );
}

function ChatPanel({
  pageContext,
  open,
  onClose,
  docked,
}: {
  pageContext: PageContext;
  open: boolean;
  onClose: () => void;
  docked?: boolean;
}) {
  const navigate = useNavigate();
  const storageKey = `${STORAGE_KEY}:${pageContext.key}`;
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? (JSON.parse(saved) as ChatMessage[]) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, streamingText]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch {
      // ignore quota / serialization errors
    }
  }, [messages, storageKey]);

  const send = async (override?: string) => {
    const text = (typeof override === "string" ? override : input).trim();
    if (!text || loading) return;

    if (!import.meta.env.VITE_OPENROUTER_API_KEY) {
      setMessages((m) => [
        ...m,
        { role: "user", content: text },
        {
          role: "assistant",
          content: "⚠️ 尚未設定 VITE_OPENROUTER_API_KEY，請檢查 .env.local",
        },
      ]);
      setInput("");
      return;
    }

    const userMsg: ChatMessage = { role: "user", content: text };
    const next: ChatMessage[] = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const apiMessages: any[] = [
        {
          role: "system",
          content: buildSystemPrompt(getCompanyId(), pageContext),
        },
        ...next.map((m) => {
          if (m.role === "tool") {
            return {
              role: "tool",
              tool_call_id: m.tool_call_id,
              name: m.name,
              content: m.content,
            };
          }
          if (m.role === "assistant" && m.tool_calls) {
            return {
              role: "assistant",
              content: m.content || "",
              tool_calls: m.tool_calls,
            };
          }
          return { role: m.role, content: m.content };
        }),
      ];

      const tools = buildTools(pageContext);
      let finished = false;
      for (let hop = 0; hop < 8; hop++) {
        setStreamingText("");
        const { content, toolCalls } = await streamOpenRouter(
          apiMessages,
          tools,
          controller.signal,
          (full) => setStreamingText(full),
        );
        setStreamingText("");

        if (toolCalls.length > 0) {
          const assistantMsg: ChatMessage = {
            role: "assistant",
            content: content ?? "",
            tool_calls: toolCalls,
          };
          apiMessages.push({
            role: "assistant",
            content: content ?? "",
            tool_calls: toolCalls,
          });
          setMessages((m) => [...m, assistantMsg]);

          for (const call of toolCalls) {
            let args: any = {};
            try {
              args = JSON.parse(call.function.arguments || "{}");
            } catch {
              args = {};
            }
            const result = await executeTool(
              call.function.name,
              args,
              pageContext,
            );
            const toolMsg: ChatMessage = {
              role: "tool",
              tool_call_id: call.id,
              name: call.function.name,
              content: result.content,
              ui: result.ui,
            };
            apiMessages.push({
              role: "tool",
              tool_call_id: call.id,
              name: call.function.name,
              content: result.content,
            });
            setMessages((m) => [...m, toolMsg]);
          }
          continue;
        }

        setMessages((m) => [...m, { role: "assistant", content: content ?? "" }]);
        finished = true;
        break;
      }

      if (!finished) {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: "⚠️ 查詢步驟過多，已停止。請換個方式描述，或縮小查詢範圍。",
          },
        ]);
      }
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `❌ 發生錯誤：${err?.message ?? String(err)}`,
        },
      ]);
    } finally {
      setLoading(false);
      setStreamingText("");
      abortRef.current = null;
    }
  };

  const stopGenerating = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
    setStreamingText("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  const handleGo = (path: string) => {
    onClose();
    navigate(path);
  };

  const clearConversation = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setInput("");
    setLoading(false);
    setStreamingText("");
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  const body = (
    <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="flex items-center gap-2">
              <img
                src={aiAgentIcon}
                alt="AI 助理"
                style={{ width: 22, height: 22, objectFit: "contain" }}
              />
              <span className="font-semibold">AI 助理</span>
              <Tag color="blue">{pageContext.label}</Tag>
            </div>
            <div className="flex items-center gap-1">
              <Popconfirm
                title="清除對話記憶"
                description="將清空目前所有對話內容，開始全新對話。確定嗎？"
                okText="清除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
                onConfirm={clearConversation}
                disabled={messages.length === 0}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  title="清除記憶 / 開始新對話"
                  disabled={messages.length === 0}
                />
              </Popconfirm>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  abortRef.current?.abort();
                  onClose();
                }}
              />
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
            <div
              className={`text-sm text-gray-500 ${
                messages.length > 0 ? "mb-3 border-b border-gray-100 pb-3" : ""
              }`}
            >
              嗨！我是「{pageContext.label}」頁面的 AI
              助理，這裡的對話記憶只屬於這個頁面。
              {isGeneralContext(pageContext)
                ? "我可以查任何資料 / 跳到任何頁面。"
                : `我只能查詢「${pageContext.label}」相關的資料，其他模組的問題請到對應頁面的 AI 助理詢問（我可以帶你過去）。`}
              試試：
              <div className="mt-3 flex flex-wrap gap-2">
                {pageContext.suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            {messages.map((m, i) => {
              if (m.role === "tool") {
                if (!m.ui) return null;
                if (
                  m.ui.kind === "navigate" &&
                  navigateDuplicatesForm(messages, i, m.ui.path)
                )
                  return null;
                return (
                  <div key={i} className="mb-3 flex justify-start">
                    <div className="max-w-[95%]">
                      {m.ui.kind === "navigate" && (
                        <NavigateCard payload={m.ui} onGo={handleGo} />
                      )}
                      {m.ui.kind === "entityList" && (
                        <EntityListCard payload={m.ui} onGo={handleGo} />
                      )}
                      {m.ui.kind === "entityDetail" && (
                        <EntityDetailCard payload={m.ui} onGo={handleGo} />
                      )}
                      {m.ui.kind === "createForm" && (
                        <CreateFormCard payload={m.ui} onGo={handleGo} />
                      )}
                      {m.ui.kind === "employeeEdit" && (
                        <EmployeeEditCard payload={m.ui} onGo={handleGo} />
                      )}
                      {m.ui.kind === "employeeStatus" && (
                        <EmployeeStatusCard payload={m.ui} onGo={handleGo} />
                      )}
                      {m.ui.kind === "bankBalance" && (
                        <BankBalanceCard payload={m.ui} onGo={handleGo} />
                      )}
                    </div>
                  </div>
                );
              }
              if (m.role === "assistant" && !m.content && m.tool_calls) {
                return (
                  <div key={i} className="mb-3 flex justify-start">
                    <div className="rounded-2xl bg-gray-100 px-3 py-2 text-xs text-gray-500">
                      🔧 呼叫工具：
                      {m.tool_calls.map((c: any) => c.function.name).join(", ")}
                    </div>
                  </div>
                );
              }
              if (m.role === "system") return null;
              return (
                <div
                  key={i}
                  className={`mb-3 flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                      m.role === "user"
                        ? "whitespace-pre-wrap bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-900"
                    }`}
                  >
                    {m.role === "user" ? (
                      m.content
                    ) : (
                      <Markdown>{m.content}</Markdown>
                    )}
                  </div>
                </div>
              );
            })}
            {loading && streamingText && (
              <div className="mb-3 flex justify-start">
                <div className="max-w-[85%] rounded-2xl bg-gray-100 px-3 py-2 text-sm text-gray-900">
                  <Markdown>{streamingText}</Markdown>
                </div>
              </div>
            )}
            {loading && !streamingText && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-gray-100 px-3 py-2">
                  <Spin size="small" />
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-3">
            <div className="flex items-end gap-2">
              <Input.TextArea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="輸入訊息，⌘/Ctrl + Enter 送出，Enter 換行"
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={loading}
              />
              {loading ? (
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={stopGenerating}
                  title="停止產生"
                />
              ) : (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => send()}
                  disabled={!input.trim()}
                />
              )}
            </div>
          </div>
      </div>
  );

  if (docked) return body;

  return (
    <Drawer
      open={open}
      onClose={() => {
        abortRef.current?.abort();
        onClose();
      }}
      placement="right"
      width={CHAT_WIDTH}
      mask={false}
      closable={false}
      destroyOnClose={false}
      styles={{
        header: { display: "none" },
        body: { padding: 0, display: "flex", flexDirection: "column" },
      }}
    >
      {body}
    </Drawer>
  );
}

// 後端 /ai 串流中的工具 part：查詢型只顯示「查詢中」，結果交由模型的 Markdown 回覆呈現；
// 寫入型顯示變更對象、筆數與失敗原因。
function BackendToolPart({ part }: { part: any }) {
  const [showDetail, setShowDetail] = useState(false);
  const { toolName, isWrite, entity, actionLabel, itemCount } =
    parseToolPart(part);
  const output = part?.output;
  const errorText = part?.errorText;
  const failedCall = part?.state === "output-error" || !!errorText;

  if (!isWrite) {
    if (output != null || failedCall) return null;
    return (
      <div className="mt-1 text-xs italic text-gray-400">
        🔧 查詢中…（{toolName}）
      </div>
    );
  }

  if (failedCall) {
    return (
      <div className="mt-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
        <div className="font-medium">
          ❌ {entity}
          {actionLabel}未完成
        </div>
        <div className="mt-1 break-all">{errorText || "工具執行失敗"}</div>
      </div>
    );
  }

  if (output == null) {
    return (
      <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
        ✏️ 正在{actionLabel}
        {entity}
        {itemCount > 0 ? ` ${itemCount} 筆` : ""}…
      </div>
    );
  }

  const { batch, total, ok, errors } = summarizeToolOutput(output);
  const allFailed = ok === 0;

  return (
    <div
      className={`mt-1 rounded-lg border px-3 py-2 text-xs ${
        allFailed
          ? "border-red-200 bg-red-50 text-red-600"
          : errors.length > 0
            ? "border-amber-200 bg-amber-50 text-amber-700"
            : "border-emerald-200 bg-emerald-50 text-emerald-700"
      }`}
    >
      <div className="font-medium">
        {allFailed ? "❌" : errors.length > 0 ? "⚠️" : "✅"} {entity}
        {actionLabel}
        {allFailed ? "失敗" : "完成"}
      </div>
      <div className="mt-1">
        {batch
          ? `成功 ${ok} 筆${errors.length > 0 ? `，失敗 ${errors.length} 筆` : ""}${
              total > 1 ? `（共 ${total} 筆）` : ""
            }`
          : `已送出處理${itemCount > 0 ? ` ${itemCount} 筆構件` : ""}`}
      </div>
      {errors.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowDetail((v) => !v)}
            className="mt-1 underline underline-offset-2"
          >
            {showDetail ? "收合失敗原因" : "查看失敗原因"}
          </button>
          {showDetail && (
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {errors.map((e, i) => (
                <li key={i} className="break-all">
                  {e}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// 由後端 /ai 端點（AI SDK useChat）驅動的對話面板，工具由 server 提供。
function BackendChatPanel({
  pageContext,
  open,
  onClose,
  docked,
  chatContext,
  onDataChanged,
}: {
  pageContext: PageContext;
  open: boolean;
  onClose: () => void;
  docked?: boolean;
  chatContext?: ChatContextInfo;
  onDataChanged?: () => void;
}) {
  const storageKey = `${STORAGE_KEY}:ai:${getBackendChatKey(pageContext.key)}`;
  const scenarioRef = useRef<BackendScenario>(
    BACKEND_SCENARIO_BY_PAGE[pageContext.key] ?? "demo",
  );
  scenarioRef.current = BACKEND_SCENARIO_BY_PAGE[pageContext.key] ?? "demo";
  const { messages, sendMessage, status, error, setMessages, stop } = useChat({
    transport: new DefaultChatTransport({
      api: `${import.meta.env.VITE_SERVER_URL}/ai`,
      credentials: "include",
      prepareSendMessagesRequest: ({ id, messages, body }) => ({
        body: {
          id,
          messages: stripStaleNodeTrees(messages as any[]),
          scenario: scenarioRef.current,
          ...body,
        },
        credentials: "include",
      }),
    }),
  });
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const restoredRef = useRef(false);
  const seenWriteCallsRef = useRef<Set<string>>(new Set());
  const onDataChangedRef = useRef(onDataChanged);
  onDataChangedRef.current = onDataChanged;

  const nodeTreeRef = useRef<{
    projectId: string;
    text: string;
    at: number;
  } | null>(null);
  const sendingRef = useRef(false);
  const projectId = chatContext?.projectId;

  const [projectAttachments, setProjectAttachments] = useState<any[]>([]);
  const { openPdf, pdfPreview } = usePdfPreview();

  useEffect(() => {
    if (!projectId) {
      setProjectAttachments([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const projectData: any = await single(
          orpcClient.project.read([{ id: projectId }]),
        );
        if (!cancelled) {
          setProjectAttachments(
            Array.isArray(projectData?.attachment) ? projectData.attachment : [],
          );
        }
      } catch (error) {
        console.error("Error fetching project attachments:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const drawingPdfIndex = useMemo(
    () => buildDrawingPdfIndex(projectAttachments),
    [projectAttachments],
  );

  const drawingPdfHandlers = useMemo<DrawingPdfHandlers>(
    () => ({
      lookup: (text: string) => lookupDrawingPdf(drawingPdfIndex, text),
      openPdf,
    }),
    [drawingPdfIndex, openPdf],
  );

  const ensureNodeTree = async () => {
    if (!projectId) return "";
    const cached = nodeTreeRef.current;
    if (
      cached &&
      cached.projectId === projectId &&
      Date.now() - cached.at < NODE_TREE_TTL_MS
    ) {
      return cached.text;
    }
    const text = await fetchProjectNodeTreeText(projectId);
    nodeTreeRef.current = { projectId, text, at: Date.now() };
    return text;
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          for (const id of collectWriteToolCallIds(parsed)) {
            seenWriteCallsRef.current.add(id);
          }
          setMessages(parsed as any);
        }
      }
    } catch {
      // ignore
    }
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let changed = false;
    for (const m of messages as any[]) {
      if (!Array.isArray(m?.parts)) continue;
      for (const part of m.parts) {
        if (typeof part?.type !== "string" || !part.type.startsWith("tool-")) {
          continue;
        }
        if (!parseToolPart(part).isWrite) continue;
        if (part?.output == null) continue;
        const callId = part.toolCallId ?? `${m.id}:${part.type}`;
        if (seenWriteCallsRef.current.has(callId)) continue;
        seenWriteCallsRef.current.add(callId);
        if (summarizeToolOutput(part.output).ok > 0) changed = true;
      }
    }
    if (changed) {
      nodeTreeRef.current = null;
      onDataChangedRef.current?.();
    }
  }, [messages]);

  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      if (messages.length > 0) {
        localStorage.setItem(
          storageKey,
          JSON.stringify(stripStaleNodeTrees(messages as any[], false)),
        );
      }
    } catch {
      // ignore quota / serialization errors
    }
  }, [messages, storageKey]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, status]);

  const loading = status === "submitted" || status === "streaming";

  // 只要還在呼叫 API、且最新一則回覆尚未串出任何文字（含工具查詢期間），
  // 就持續顯示 loading 圖示，讓使用者知道系統正在處理。
  const lastMessage = messages[messages.length - 1] as any;
  const lastAssistantHasText =
    lastMessage?.role === "assistant" &&
    Array.isArray(lastMessage.parts) &&
    lastMessage.parts.some(
      (p: any) =>
        p?.type === "text" &&
        typeof p.text === "string" &&
        p.text.trim().length > 0,
    );
  const showThinking = loading && !lastAssistantHasText;

  const send = async (override?: string) => {
    const text = (typeof override === "string" ? override : input).trim();
    if (!text || loading || sendingRef.current) return;
    sendingRef.current = true;
    setInput("");
    try {
      const nodeTree = await ensureNodeTree();
      sendMessage({
        text: `${WRITE_DIRECTIVE}${buildContextBlock(chatContext, nodeTree)}${text}`,
      });
    } finally {
      sendingRef.current = false;
    }
  };

  const lastAssistantText =
    lastMessage?.role === "assistant" && Array.isArray(lastMessage.parts)
      ? lastMessage.parts
          .filter((p: any) => p?.type === "text" && typeof p.text === "string")
          .map((p: any) => p.text)
          .join("\n")
      : "";

  const pendingConfirm = loading ? null : parseConfirmRequest(lastAssistantText);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  const clearConversation = () => {
    stop?.();
    setMessages([]);
    setInput("");
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  const body = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <img
            src={aiAgentIcon}
            alt="AI 助理"
            style={{ width: 22, height: 22, objectFit: "contain" }}
          />
          <span className="font-semibold">AI 助理</span>
          {chatContext?.node ? (
            <Tag
              color="blue"
              style={{
                marginInlineEnd: 0,
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={chatContext.node.name}
            >
              {chatContext.node.name}
            </Tag>
          ) : (
            messages.length === 0 && <Tag color="blue">{pageContext.label}</Tag>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Popconfirm
            title="清除對話記憶"
            description="將清空目前所有對話內容，開始全新對話。確定嗎？"
            okText="清除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            onConfirm={clearConversation}
            disabled={messages.length === 0}
          >
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              title="清除記憶 / 開始新對話"
              disabled={messages.length === 0}
            />
          </Popconfirm>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => {
              stop?.();
              onClose();
            }}
          />
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        <div
          className={`text-sm text-gray-500 ${
            messages.length > 0 ? "mb-3 border-b border-gray-100 pb-3" : ""
          }`}
        >
          嗨！我是「{pageContext.label}」頁面的 AI 助理。試試：
          <div className="mt-3 flex flex-wrap gap-2">
            {pageContext.suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => send(s)}
                className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        {(messages as any[]).map((m, i) => {
          const isUser = m.role === "user";
          const parts: any[] = Array.isArray(m.parts) ? m.parts : [];
          return (
            <div
              key={m.id ?? i}
              className={`mb-3 flex ${isUser ? "justify-end" : "justify-start"}`}
            >
              <div className={isUser ? "max-w-[85%]" : "max-w-[95%]"}>
                {parts.map((part, pi) => {
                  if (part?.type === "text") {
                    if (isUser) {
                      return (
                        <div
                          key={pi}
                          className="whitespace-pre-wrap rounded-2xl bg-blue-500 px-3 py-2 text-sm text-white"
                        >
                          {stripContextBlock(part.text ?? "")}
                        </div>
                      );
                    }
                    const shown = stripConfirmMarker(part.text ?? "");
                    if (!shown) return null;
                    return (
                      <div
                        key={pi}
                        className="rounded-2xl bg-gray-100 px-3 py-2 text-sm text-gray-900"
                      >
                        <Markdown>{shown}</Markdown>
                      </div>
                    );
                  }
                  if (
                    typeof part?.type === "string" &&
                    part.type.startsWith("tool-")
                  ) {
                    return <BackendToolPart key={pi} part={part} />;
                  }
                  return null;
                })}
              </div>
            </div>
          );
        })}
        {pendingConfirm && (
          <div className="mb-3 flex justify-start">
            <div className="max-w-[95%] rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2">
              <div className="text-xs text-amber-700">
                ⚠️ 需要你確認後才會執行
                {pendingConfirm.summary ? `：${pendingConfirm.summary}` : ""}
              </div>
              <div className="mt-2 flex gap-2">
                <Button
                  size="small"
                  type="primary"
                  danger
                  onClick={() => send("確認執行")}
                >
                  確認執行
                </Button>
                <Button size="small" onClick={() => send("取消，不要執行")}>
                  取消
                </Button>
              </div>
            </div>
          </div>
        )}
        {showThinking && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-2xl bg-gray-100 px-3 py-2 text-sm text-gray-500">
              <Spin size="small" />
              思考中…
            </div>
          </div>
        )}
        {error && (
          <div className="mb-3 flex justify-start">
            <div className="max-w-[85%] rounded-2xl bg-red-50 px-3 py-2 text-sm text-red-600">
              ❌ 發生錯誤：{error.message}
            </div>
          </div>
        )}
      </div>

      <div className="border-t p-3">
        {chatContext && (
          <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
            <span className="shrink-0">目前選取節點</span>
            {chatContext.node ? (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {chatContext.node.name}
                {nodeTypeLabel(chatContext.node.type)
                  ? `（${nodeTypeLabel(chatContext.node.type)}）`
                  : ""}
              </Tag>
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>未選取（以整個專案為範圍）</Tag>
            )}
          </div>
        )}
        <div className="mb-2 text-right text-xs text-amber-600">
          ⚠️ AI 可新增／修改／刪除資料
        </div>
        <div className="flex items-end gap-2">
          <Input.TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="輸入訊息，⌘/Ctrl + Enter 送出，Enter 換行"
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading}
          />
          {loading ? (
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={() => stop?.()}
              title="停止產生"
            />
          ) : (
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => send()}
              disabled={!input.trim()}
            />
          )}
        </div>
      </div>
    </div>
  );

  const withPdfPreview = (content: React.ReactNode) => (
    <DrawingPdfContext.Provider value={drawingPdfHandlers}>
      {content}
      {pdfPreview}
    </DrawingPdfContext.Provider>
  );

  if (docked) return withPdfPreview(body);

  return withPdfPreview(
    <Drawer
      open={open}
      onClose={() => {
        stop?.();
        onClose();
      }}
      placement="right"
      width={CHAT_WIDTH}
      mask={false}
      closable={false}
      destroyOnClose={false}
      styles={{
        header: { display: "none" },
        body: { padding: 0, display: "flex", flexDirection: "column" },
      }}
    >
      {body}
    </Drawer>,
  );
}
